import React, { useEffect, useMemo } from "react"
import moment from "moment"

import socketIOClient from "socket.io-client"
import { loadMercadoPago } from "@mercadopago/sdk-js"

import { useState } from "react"
import { Button, Label } from "reactstrap"
import { ErrorBoundary } from "react-error-boundary"

import "moment/locale/pt-br"

//Contexts
import TabContext from "./contexts/TabContext"
import MenuContext from "./contexts/MenuContext"
import UserContext from "./contexts/UserContext"
import OrderContext from "./contexts/OrderContext"
import SocketContext from "./contexts/SocketContext"
import LoadingContext from "./contexts/LoadingContext"
import CompanyContext from "./contexts/CompanyContext"
import SettingsContext from "./contexts/SettingsContext"
import MercadoPagoContext from "./contexts/MercadoPagoContext"

import { Toaster } from "react-hot-toast"

//Controllers
// import UserController from "./controllers/UserController"
import OrderController from "./controllers/OrderController"
// import PopupController from "./controllers/PopupController"
// import ComboController from "./controllers/ComboController"
// import ProductController from "./controllers/ProductController"
import CompanyController from "./controllers/CompanyController"
// import BenefitController from "./controllers/BenefitController"
// import SettingController from "./controllers/SettingController"
import SupportController from "./controllers/SupportController"
// import FidelityController from "./controllers/FidelityController"
import CustomerController from "./controllers/CustomerController"
// import BeverageController from "./controllers/BeverageController"
import ScheduleController from "./controllers/ScheduleController"
import CredentialController from "./controllers/CredentialController"
// import HighlightsController from "./controllers/HighlightsController"
import DeliveryFeeController from "./controllers/DeliveryFeeController"
// import DeliveryAreasController from "./controllers/DeliveryAreasController"
import MercadoPagoController from "./controllers/MercadoPagoController"
import PaymentsController from "./controllers/PaymentsController"
import SiteEventController from "./controllers/SiteEventController"

// Helpers
import { getDiscountOrder, getParamsToCreateOrder, getTotalOrder, computeApiValSubtotal } from "./helpers/OrderTools"
import {
  getBeverageName,
  // getProductName
} from "./helpers/BuilderTools"
import {
  userIsConnected,
  getLocalUser,
  getCustomerEmailPhone,
  // getFidelityProgress
} from "./helpers/UserTools"
import { syncCashbackPayment as syncCashbackPaymentHelper } from "./helpers/CashbackHelpers"
import GeneralTools, {
  formatBoolean,
  onMessageDialog,
  formatFloat,
  getFormatDate,
  // getUrlAWSManagerFile,
  escapeSpecialChart,
  generateUuid,
} from "./helpers/GeneralTools"
// import { checkBetweenIntervalPeriod } from "./helpers/DateTools"
import {
  getCity,
  getLabelAddress,
  getNeighbourhood,
  getState,
  getStates,
  getStreetName,
  getDistanceCoordinates,
  getMapboxSuggestionsByName,
  getAddressCoordinates,
  getCitiesByState,
  isSameAddressArea,
  buildSyntheticAddressFeature,
} from "./helpers/AddressessTools"
import {
  encrypt,
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
  getCookie,
  setCookie,
  removeCookie,
} from "./helpers/StorageTools"

import ModalClientAddressNotFound from "./views/modals/ModalClientAddressNotFound/ModalClientAddressNotFound"

// Sytles
import logoImage from "./assets/img/brand/logo.webp"
import "./App.scss"

import FacebookPixelManager, { trackPixelEvent, isPixelActive } from "./components/FacebookPixelManager"
import FullContentLoading from "./components/FullContentLoading"

// Footer não é lazy pois é pequeno, sempre visível e crítico para navegação
import Footer from "./components/Footer"

import Message from "./components/Message"
import HomeSkeleton from "./components/HomeSkeleton"
import useInitialLoad from "./hooks/useInitialLoad"

// Header contém o banner (LCP da home) — import estático para não adicionar
// um round-trip de chunk no caminho crítico de renderização
import Header from "./components/Header"

// Componentes da UI (alguns podem ser sempre visíveis, outros podem ser lazy-carregados)
const OrderResumeBar = React.lazy(() => import("./components/OrderResumeBar"))

// Componentes de Páginas/Seções - ALTAMENTE RECOMENDADO O LAZY LOAD
const Main = React.lazy(() => import("./views/sections/Main"))
const Highlights = React.lazy(() => import("./views/sections/Highlights"))
const Combos = React.lazy(() => import("./views/sections/Combos"))
const Products = React.lazy(() => import("./views/sections/Products"))
const Beverages = React.lazy(() => import("./views/sections/Beverages"))
const Profile = React.lazy(() => import("./components/Profile/Profile"))

// Modais (já transformados no exemplo anterior)
const ModalBuilder = React.lazy(() => import("./views/modals/ModalBuilder"))
const OrderPreview = React.lazy(() => import("./components/OrderPreview/OrderPreview")) // É um modal, faz sentido ser lazy

// Modals
const ModalPopup = React.lazy(() => import("./views/modals/ModalPopup"))
const ModalSignIn = React.lazy(() => import("./views/modals/ModalSignIn"))
const ModalBlocked = React.lazy(() => import("./views/modals/ModalBlocked"))
const ModalCompanies = React.lazy(() => import("./views/modals/ModalCompanies"))
const ModalBeverages = React.lazy(() => import("./views/modals/ModalBeverages"))
const ModalSuccessOrder = React.lazy(() => import("./views/modals/ModalSuccessOrder"))
const ModalOrderFinisher = React.lazy(() => import("./views/modals/ModalOrderFinisher/ModalOrderFinisher"))
const ModalOrderProcess = React.lazy(() => import("./views/modals/ModalOrderProcess/ModalOrderProcess"))
const ModalOrderSchedule = React.lazy(() => import("./views/modals/ModalOrderSchedule/ModalOrderSchedule"))
const ModalInitialAddress = React.lazy(() => import("./views/modals/ModalInitialAddress/ModalInitialAddress"))
const ModalMapAddress = React.lazy(() => import("./views/modals/ModalMapAddress/ModalMapAddress"))
const ModalNumberAddress = React.lazy(() => import("./views/modals/ModalNumberAddress/ModalNumberAddress"))
const ModalFinalAddress = React.lazy(() => import("./views/modals/ModalFinalAddress/ModalFinalAddress"))
const ModalCompanyContacts = React.lazy(() => import("./views/modals/ModalCompanyContacts/ModalCompanyContacts"))
const ModalAddressNotFound = React.lazy(() => import("./views/modals/ModalAddressNotFound/ModalAddressNotFound"))
const ModalPixPayment = React.lazy(() => import("./views/modals/ModalPixPayment/ModalPixPayment"))
const ModalMinimapAddress = React.lazy(() => import("./views/modals/ModalMinimapAddress/ModalMinimapAddress"))

moment.locale("pt-br")

const App = () => {
  const version = process.env.REACT_APP_VERSION

  const [errorMessage, setErrorMessage] = React.useState([])

  const [loading, setLoading] = React.useState(true)
  const [isContentLoading, setIsContentLoading] = React.useState(true)
  const [forceLoading, setForceLoading] = React.useState(false)
  const [addressesLoading, setAddressesLoading] = React.useState(false)

  const [order, setOrder] = React.useState({
    customer: null,
    combos: [],
    products: [],
    beverages: [],
    payments: [],
    benefits: [],
    total: 0.0,
    subTotal: 0.0,
    discount: 0,
    discountType: "R$",
    deliveryFee: 0,
    deliveryComments: "",
  })
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState({
    id_payment_method: -1,
  })

  const [currentOrderCombo, setCurrentOrderCombo] = React.useState(0)

  const [user, setUser] = React.useState(null)
  const [socket, setSocket] = React.useState(null)
  const [company, setCompany] = React.useState({})

  const [tabSelected, setTabSelected] = React.useState("home")
  const [hideTabs, setHideTabs] = React.useState(false)

  const [menuFixed, setMenuFixed] = React.useState(false)
  const [menuOptions, setMenuOptions] = React.useState([])

  const [combos, setCombos] = React.useState([])
  const [settings, setSettings] = React.useState([])
  const [products, setProducts] = React.useState([])
  const [popup, setPopup] = React.useState(null)
  const [highlights, setHighlights] = React.useState([])
  const [beverages, setBeverages] = React.useState([])
  const [schedules, setSchedules] = React.useState([])
  const [companies, setCompanies] = React.useState([])
  const [fidelities, setFidelities] = React.useState([])
  const [modality, setModality] = React.useState(1) // 1 - entreta / 2 - retirada
  const [currentStep, setCurrentStep] = React.useState({ step: 0, date: "00:00" })

  const [deliveryType, setDeliveryType] = useState("direct")

  const [isOutsideCoverageArea, setIsOutsideCoverageArea] = React.useState(false)
  const [finalizingOrder, setFinalizingOrder] = React.useState(false)

  const [states, setStates] = React.useState([])
  const [neighbourhoods, setNeighbourhoods] = React.useState([])

  const [userSelectedAddress, setUserSelectedAddress] = React.useState(null)
  const [modalContentOrder, setModalContentOrder] = React.useState(false)
  const [modalSignIn, setModalSignIn] = React.useState({ status: false })
  const [modalOrderFinisher, setModalOrderFinisher] = React.useState(false)
  const [modalOrderProcess, setModalOrderProcess] = React.useState({
    status: false,
    content: null,
    resend: false,
    newRequest: false,
    scheduleInfo: null,
  })
  const [modalOrderPreview, setModalOrderPreview] = React.useState(false)
  const [modalOrderSchedule, setModalOrderSchedule] = React.useState(false)
  const [modalCompanyContacts, setModalCompanyContacts] = React.useState(false)
  const [modalCompanies, setModalCompanies] = React.useState({ status: false, content: null, earlyAddress: null })
  const [modalPaymentMethods, setModalPaymentMethods] = useState({ status: false, content: null })
  const [modalBuilder, setModalBuilder] = useState({ status: false, type: "product", content: null })
  const [modalBeverages, setModalBeverages] = useState({ status: false, content: null })
  const [modalSuccessOrder, setModalSuccessOrder] = useState({ status: false, content: null })
  const [modalPopup, setModalPopup] = React.useState({ status: true })
  const [modalBlockByOutdatedVersion, setModalBlockByOutdatedVersion] = React.useState({ status: false, content: null })
  const [modalClientAddressNotFound, setModalClientAddressNotFound] = React.useState({ status: false, address: null })
  const [modalAddressNotFound, setModalAddressNotFound] = React.useState({ status: false, title: "", companies: [] })
  const [modalPixPayment, setModalPixPayment] = React.useState({ status: false, content: null })
  const [modalMinimapAddress, setModalMinimapAddress] = React.useState({ status: false, content: null })

  const [modalInitialAddress, setModalInitialAddress] = React.useState({ status: false, content: null })
  const [modalMapAddress, setModalMapAddress] = React.useState({ status: false, location: {} })
  const [modalNumberAddress, setModalNumberAddress] = React.useState({ status: false, address: {} })
  const [modalFinalAddress, setModalFinalAddress] = React.useState({ status: false, address: {}, editNeighbourhood: false })
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = React.useState(false)
  const [recoverySettings, setRecoverySettings] = React.useState({})

  const [colors, setColors] = React.useState([
    { id: 1, used: false, color: "#F2D7D5" },
    { id: 2, used: false, color: "#D4E6F1" },
    { id: 3, used: false, color: "#D4EFDF" },
    { id: 4, used: false, color: "#FDEBD0" },
    { id: 5, used: false, color: "#FADBD8" },
    { id: 6, used: false, color: "#EBDEF0" },
    { id: 7, used: false, color: "#D6EAF8" },
    { id: 8, used: false, color: "#D1F2EB" },
    { id: 9, used: false, color: "#D6DBDF" },
    { id: 10, used: false, color: "#F4ECF7" },
    { id: 11, used: false, color: "#FAE5D3" },
  ])
  const [updateOrders, setUpdateOrders] = React.useState(false)
  const [activePixelId, setActivePixelId] = React.useState({ companies: [], pixelId: null })

  const companyRef = React.useRef({ uuid_company: null })
  const menuOptionsRef = React.useRef(menuOptions)
  const deliveryFeesRef = React.useRef(null)
  // const deliveryAreasRef = React.useRef(null)
  const orderSuccessId = React.useRef(null)
  const popupAlreadyOpen = React.useRef(false)
  const goToOrderProgressRef = React.useRef({ status: false, orderUid: null, scheduleOrderUid: null })
  const modalPixPaymentContent = React.useRef(null)
  const goToFidelityRef = React.useRef(false)
  const goToCashbackRef = React.useRef(false)
  const goToOrdersRef = React.useRef(false)
  const editAddressRef = React.useRef(null)
  // const menuFixedRef = React.useRef(menuFixed)
  const finalizingOrderRef = React.useRef(false)
  const userRef = React.useRef(null)
  const scheduleInfoRef = React.useRef(null)
  const userSelectedAddressRef = React.useRef(null)
  const groupFidelitiesRef = React.useRef({})
  const showOrderProgressContextRef = React.useRef({ status: false, orderUid: null, scheduleOrderUid: null })
  const defaultPaymentMethodIdRef = React.useRef({
    id_payment_method: -1,
    nm_payment_method: "Sem forma de pagamento",
  })
  // Session ID para rastreamento de eventos do site
  const siteSessionId = React.useRef(
    (() => {
      let sid = localStorage.getItem("site_session_id")
      if (!sid) {
        sid = generateUuid()
        localStorage.setItem("site_session_id", sid)
      }
      return sid
    })()
  )

  // Objeto para armazenar a instância do mercado pago
  const [MercadoPagoInstance, setMercadoPagoInstance] = React.useState({})

  const { fetchInitialData } = useInitialLoad({
    setCompany,
    setSettings,
    setProducts,
    setCombos,
    setBeverages,
    setHighlights,
    setSchedules,
    setFidelities,
    setModalInitialAddress,
    setModalCompanies,
    setOrder,
    setCurrentOrderCombo,
    setLoading,
    setIsContentLoading,
    setUser,
    setUserSelectedAddress,
    setModality,
    setCompanies,
    companyRef,
    userRef,
    userSelectedAddressRef,
    groupFidelitiesRef,
    menuOptionsRef,
    defaultPaymentMethodIdRef,
    setSelectedPaymentMethod,
    setMenuOptions,
    modality,
    userSelectedAddress,
    modalCompanies,
    removeCookie,
  })

  /* eslint-disable */
  React.useEffect(() => {
    setLoading(true)

    const searchParams = new URLSearchParams(document.location.search)

    const customerUid = searchParams.get("c")
    const expiresIn = searchParams.get("e")

    if (customerUid !== null || expiresIn !== null) {
      setRecoverySettings({ customerUid: customerUid, expiresIn: expiresIn })
      setModalSignIn({ status: true })
    }

    //Verificando se o cliente está na versão atual correta
    //Caso não esteja, zerar o localstorage e cookies para evitar erros
    verifyWebsiteVersion().then()

    getCookie("publicToken")
      .then(async (publicToken) => {
        let storageCompany = getLocalItem("company", true)
        let storageCompanyIds = getLocalItem("companyIds", true)

        let hostStorage = getLocalItem("siteHost")

        if (!publicToken || hostStorage !== window.location.host) {
          // Obtendo as credenciais do assinante
          CredentialController.fetchOne().then((responseCredentials) => {
            if (responseCredentials.status === 200) {
              const [subscriberUid, accessToken, database, companyIds = []] = responseCredentials.data
              if (hostStorage !== window.location.host) {
                storageCompany = null
              }

              publicToken = accessToken
              setLocalItem("database", database)
              setLocalItem("companyIds", companyIds, true)
              setLocalItem("siteHost", window.location.host)
              setCookie("subscriberUid", subscriberUid).then()
              setCookie("publicToken", publicToken).then(() => {
                setOnStartInfo(publicToken, storageCompany, companyIds)
              })
            } else {
              setLoading(false)
              setErrorMessage([
                "Não foi possível carregar o conteúdo do site.",
                "Favor atualizar a página! ",
                "Caso o erro persista, favor reportar imediatamente ao estabelecimento! ",
              ])
            }
          })
        } else {
          setOnStartInfo(publicToken, storageCompany, storageCompanyIds)
        }
      })
      .catch((error) => {
        console.error("ERROR", error)
        setLoading(false)
        setErrorMessage(["Não foi possível carregar o conteúdo do site.", "Favor reportar imediatamente ao estabelecimento! "])
      })

    // Eventos de rolagem da página
    document.addEventListener("scroll", (e) => {
      for (const { id } of menuOptionsRef.current) {
        if (!!document.getElementById(id)) {
          // Definindo o valor da seção de acordo com o tamanho e posição na página
          const offset = document.getElementById(id).offsetTop + document.getElementById(id).offsetHeight - 200
          // Se for maior significa que está sendo mostrado
          if (offset > document.getElementById("section-home").scrollTop) {
            const newMenuOptions = menuOptionsRef.current.map((item) => {
              return { ...item, focus: item.id === id }
            })

            // Mudando o item do menu ativo
            menuOptionsRef.current = [...newMenuOptions]

            break
          }
        }
      }
    })
  }, [])

  React.useEffect(() => {
    showRecentPendingPayment()
  }, [userRef.current])

  React.useEffect(() => {
    const popupStorage = getLocalItem("popups", true)
    if (!!popupStorage) {
      const companyPopup = popupStorage?.data?.rows?.find(
        ({ fk_id_company, fk_id_subscriber }) =>
          company.id_company === fk_id_company && company?.subscriber?.id_subscriber === fk_id_subscriber && !modalCompanies.status
      )
      if (!!companyPopup && !popupAlreadyOpen.current) {
        popupAlreadyOpen.current = true
        showPopupMessage(companyPopup, companyPopup)
      }
    }
  }, [company])

  React.useEffect(() => {
    getDiscountType()
  }, [modality])

  const verifyWebsiteVersion = async () => {
    const buildVersion = process.env.REACT_APP_VERSION
    const currentWebsiteVersion = await SupportController.getWebsiteVersion()
    if (currentWebsiteVersion.status === 200) {
      const currentWebsiteVersionList = (currentWebsiteVersion?.data?.nro_version || "0.0.0").split(".").map(Number)
      const buildVersionList = buildVersion.split(".").map(Number)

      // if (currentWebsiteVersion.data.nro_version !== buildVersion) {
      if (
        currentWebsiteVersionList[0] !== buildVersionList[0] ||
        currentWebsiteVersionList[1] !== buildVersionList[1] ||
        buildVersionList[2] < currentWebsiteVersionList[2]
      ) {
        setModalBlockByOutdatedVersion({
          status: true,
          content: {
            buildVersion,
            currentVersion: currentWebsiteVersion.data.nro_version,
          },
        })
      }
    } else {
      setModalBlockByOutdatedVersion({
        status: true,
        content: {
          buildVersion,
          currentVersion: null,
          getWebsiteVersionFailed: true,
        },
      })
    }
  }

  const showRecentPendingPayment = async () => {
    if (!!userRef.current) {
      const pendingPaymentsRequest = await PaymentsController.getRecentPendingPayments()

      if (pendingPaymentsRequest.status === 200 && !modalPixPayment.status) {
        const pendingPayment = pendingPaymentsRequest.data

        const paymentInfoRequest = await PaymentsController.getPaymentInfo(pendingPayment.payment_id)

        if (paymentInfoRequest.status === 200) {
          setModalPixPayment({ status: true, content: paymentInfoRequest.data })
        }
      }
    }
  }

  // Buscar configurações de desconto por modalidade
  const getDiscountType = (modalityOrder, changeOrder) => {
    let newOrder = { ...order }
    let discountType = "R$"
    let discount = "0,00"

    let newModality = !!modalityOrder ? modalityOrder : modality
    // if (!!modality) {
    switch (newModality) {
      case 1:
        if (!!settings.delivery_discount_type) {
          discountType = settings.delivery_discount_type
          newOrder = { ...newOrder, discountType }
        }

        if (!!settings.delivery_discount_value) {
          const [total, subTotal] = getTotalOrder(newOrder, userSelectedAddress?.deliveryFee)
          const discount = parseFloat(settings.delivery_discount_value)
          newOrder = { ...newOrder, discount, ds_discount: discount > 0 ? "Desconto de modalidade" : "" }
        }
        if (changeOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
      case 2:
        if (!!settings.pickup_discount_type) {
          discountType = settings.pickup_discount_type
          newOrder = { ...newOrder, discountType }
        }

        if (!!settings.pickup_discount_value) {
          const discount = parseFloat(settings.pickup_discount_value)
          newOrder = { ...newOrder, discount, ds_discount: discount > 0 ? "Desconto de modalidade" : "" }
        }
        if (changeOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
      case 3:
        if (!!settings.indoor_discount_type) {
          discountType = settings.indoor_discount_type

          newOrder = { ...newOrder, discountType }
        }

        if (!!settings.indoor_discount_type) {
          const discount = parseFloat(settings.indoor_discount_value)
          newOrder = { ...newOrder, discount, ds_discount: discount > 0 ? "Desconto de modalidade" : "" }
        }
        if (changeOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
      default:
        newOrder = { ...newOrder, discountType, discount }
        if (setOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
    }
    // }
  }

  // Busca de pedidos por agendamentos
  const getScheduleOrders = async () => {
    const dateNow = new Date()
    const dateStartEnd = new Date()
    dateStartEnd.setDate(dateStartEnd.getDate() + 7)

    const responseScheduleOrders = await ScheduleController.fetchAllScheduleOrders({
      startDate: moment(dateNow).format("YYYY-MM-DD"),
      endDate: moment(dateStartEnd).format("YYYY-MM-DD"),
    })

    if (responseScheduleOrders.status === 200) {
      return responseScheduleOrders.data.rows || []
    }

    onMessageDialog("danger")
      .fire({
        title: "Erro ao buscar dados dos agendamentos do estabelecimento",
        icon: "error",
        showConfirmButton: true,
        confirmButtonText: "Aceitar",
        showCancelButton: true,
        cancelButtonText: "Recusar",
        reverseButtons: true,
      })
      .then()

    return []
  }

  // Busca dos dias dos agendamentos e seus horários ( Storage / Banco )
  const getSchedules = async (new_st_open = null, withOrders = false, config_orders) => {
    const weekdayDefault = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"]

    const now_day = new Date().getDay() // Dia atual ( Hoje )

    let schedules = []
    let ordersSchedules = []

    const schedulesSession = await getLocalItem("schedules", true)

    if (!!schedulesSession) {
      const scheduleList = schedulesSession.map(({ uuid_schedule, day_of_week, st_open, work_times, neighbourhoods }) => {
        return {
          uuid_schedule,
          day_of_week,
          st_open:
            new_st_open !== null
              ? day_of_week === now_day
                ? new_st_open
                : st_open
              : day_of_week === now_day
              ? config_orders
                ? company?.st_website
                : st_open
              : st_open,
          work_times,
          neighbourhoods,
          name: weekdayDefault[day_of_week],
        }
      })
      ordersSchedules = await getScheduleOrders()
      schedules = scheduleList
    } else {
      await ScheduleController.fetchAll()
        .then(async (responseSchedule) => {
          if (responseSchedule.status === 200) {
            const scheduleList = responseSchedule.data.map(({ uuid_schedule, day_of_week, st_open, work_times, neighbourhoods }) => {
              return {
                uuid_schedule,
                day_of_week,
                st_open:
                  new_st_open !== null
                    ? day_of_week === now_day
                      ? new_st_open
                      : st_open
                    : day_of_week === now_day
                    ? config_orders
                      ? company?.st_website
                      : st_open
                    : st_open,
                work_times,
                neighbourhoods,
                name: weekdayDefault[day_of_week],
              }
            })

            schedules = scheduleList

            if (withOrders) {
              ordersSchedules = await getScheduleOrders()
            }
          } else {
            onMessageDialog("danger")
              .fire({
                title: "Erro ao buscar dados de agendamento",
                icon: "error",
                showConfirmButton: true,
                confirmButtonText: "Aceitar",
                showCancelButton: true,
                cancelButtonText: "Recusar",
                reverseButtons: true,
              })
              .then()
          }
        })
        .catch(() => {
          onMessageDialog("danger")
            .fire({
              title: "Erro ao buscar dados de agendamento",
              icon: "error",
              showConfirmButton: true,
              confirmButtonText: "Aceitar",
              showCancelButton: true,
              cancelButtonText: "Recusar",
              reverseButtons: true,
            })
            .then()
        })
    }

    return { schedules, ordersSchedules }
  }

  // Dados do socket
  const setOnSocket = (publicToken, database) => {
    const socket = socketIOClient(process.env.REACT_APP_CF_SOCKET_HOST, {
      transports: ["websocket"],
      query: { token: publicToken, origin: "site-subscriber", database },
    })

    socket.on("store_openned", async () => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [store_openned]")
      onChangeStatusCompany(true)

      // Abrindo a loja no dia, alteramos os status do dia para fazer agendamentos
      const { schedules } = await getSchedules(true)

      if (!!schedules) {
        setLocalItem("schedules", schedules, true)
      }
    })

    socket.on("store_closed", async () => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [store_closed]")
      onChangeStatusCompany(false)

      // Alterando status do dia atual para false ( caso a loja feche no meio da operação )
      const { schedules } = await getSchedules(!formatBoolean(settings?.not_allow_order_schedule_with_closed_company || false))

      if (!!schedules) {
        setLocalItem("schedules", schedules, true)
      }
    })

    socket.on("online_payment_approved", async (socketInfo) => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [online_payment_approved]")
      if (companyRef.current?.id_company === socketInfo.fk_id_company && socketInfo.uuid_customer === userRef.current?.uuid_customer) {
        goToOrderProggressAfterOnlinePayment(socketInfo.order.uuid_order)
      }
    })

    socket.on("online_payment_schedule_approved", async (socketInfo) => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [online_payment_schedule_approved]")
      if (companyRef.current?.id_company === socketInfo.fk_id_company && socketInfo.uuid_customer === userRef.current?.uuid_customer) {
        goToScheduleAfterOnlinePayment(socketInfo.schedule)
      }
    })

    // //pedido aprovado
    // socket.on("approve_orders", (socketinfo) => {
    //   if (company.id_company === socketinfo.companyId || companyRef.current.id_company === socketinfo.companyId) {
    //     console.info("RECEBENDO DADOS DO SOCKET [Home] [approve_orders]")
    //     const newDate = new Date()
    //     setCurrentStep({
    //       step: 1,
    //       date: `${newDate.getHours()}:${newDate.getMinutes()}`,
    //       orderId: socketinfo.orderId,
    //     })
    //   }
    // })
    //
    // //motoboy iniciou a corrida
    // socket.on("deliveryman_order_started", (socketinfo) => {
    //   if (company.id_company === socketinfo.companyId || companyRef.current.id_company === socketinfo.companyId) {
    //     console.info("RECEBENDO DADOS DO SOCKET [deliveryman] [deliveryman_order_started]")
    //     const newDate = new Date()
    //     setCurrentStep({
    //       step: 2,
    //       date: `${newDate.getHours()}:${newDate.getMinutes()}`,
    //       orderId: socketinfo.orderId,
    //       userId: socketinfo.userId,
    //     })
    //   }
    // })
    //
    // //motoboy se movendo
    // socket.on("deliveryman_moved", ({ user }) => {
    //   //Verificando se o motoboy é vinculado a esta empresa
    //   const userCompany = user.companies.find(({ id_company }) => {
    //     return company.id_company === id_company || companyRef.current.id_company === id_company
    //   })
    //   if (!!userCompany) {
    //     // console.info("RECEBENDO DADOS DO SOCKET [App] [deliveryman_moved]")
    //     setCurrentStep({
    //       ...currentStep,
    //       deliverymanPos: {
    //         latitude: user.latitude,
    //         longitude: user.longitude,
    //       },
    //       userId: user.id_user,
    //       userOrdersInRoute: user.orders_in_route,
    //     })
    //   }
    // })
    //
    // //Motoboy encerrou o pedido
    // socket.on("deliveryman_order_finished", (socketinfo) => {
    //   if (company.id_company === socketinfo.companyId || companyRef.current.id_company === socketinfo.companyId) {
    //     console.info("RECEBENDO DADOS DO SOCKET [deliveryman] [deliveryman_order_finished]")
    //     const newDate = new Date()
    //     setCurrentStep({
    //       step: 3,
    //       date: `${newDate.getHours()}:${newDate.getMinutes()}`,
    //       orderId: socketinfo.orderId,
    //     })
    //   }
    // })
    //
    // //Estabelecimento encerrou o pedido
    // socket.on("finish_orders", (socketinfo) => {
    //   if (company.id_company === socketinfo.companyId || companyRef.current.id_company === socketinfo.companyId) {
    //     console.info("RECEBENDO DADOS DO SOCKET [Home] [finish_orders]")
    //     const newDate = new Date()
    //     setCurrentStep({
    //       step: 3,
    //       date: `${newDate.getHours()}:${newDate.getMinutes()}`,
    //       orderId: socketinfo.orderId,
    //     })
    //   }
    // })

    //Estabelecimento atualizou o tempo de retirada
    // socket.on("pickup_time_updated", (socketinfo) => {
    //   if (company.id_company === socketinfo.companyId || companyRef.current.id_company === socketinfo.companyId) {
    //     console.info("RECEBENDO DADOS DO SOCKET [Home] [pickup_time_updated]")
    //     setSettings({ ...settings, pickup_time: socketinfo.pickup_time })
    //   }
    // })

    setSocket(socket)
  }

  const goToOrderProggressAfterOnlinePayment = async (uuidOrder) => {
    clearOrder()
    Message.success("Pedido confirmado com sucesso!")
    setModalPixPayment({ status: false, content: null })

    const actualOrder = await OrderController.fetchOne(uuidOrder)

    setTabSelected("profile")

    showOrderProgressContextRef.current = { status: true, orderUid: uuidOrder, content: actualOrder.data }
    setUpdateOrders(!updateOrders)
  }

  //Confirmação de pagamento online de um pedido agendado
  const goToScheduleAfterOnlinePayment = (schedule = null) => {
    clearOrder()
    setModalPixPayment({ status: false, content: null })

    onMessageDialog("sucess")
      .fire({
        title: "Pagamento confirmado — pedido agendado!",
        icon: "success",
        timer: 3000,
        showCancelButton: false,
        showConfirmButton: false,
      })
      .then(({ isDismissed }) => {
        if (isDismissed) {
          showOrderProgressContextRef.current = { status: false, scheduleOrderUid: schedule?.uuid_schedule_order || null }
          setTabSelected("profile")
        }
      })

    setUpdateOrders(!updateOrders)
  }

  const setOnStartInfo = async (publicToken, storageCompany = null, companyIds = []) => {
    const database = getLocalItem("database")
    const menuOptionsStorage = getLocalItem("menuOptions", true)

    if (menuOptionsStorage) {
      setMenuOptions(menuOptionsStorage)
      menuOptionsRef.current = menuOptionsStorage
    }

    console.info("CONECTANDO NO SOCKET...")
    setOnSocket(publicToken, database)

    console.info("CONSULTANDO DADOS DE ESTABELECIMENTOS...")
    let newCompany = storageCompany

    const searchParams = new URLSearchParams(document.location.search)

    const whatsappNumber = searchParams.get("wn")
    const companyIdString = searchParams.get("cId")

    if (whatsappNumber !== null) {
      setForceLoading(true)

      if (!!newCompany) {
        const whatsappCompanyId = parseInt(companyIdString)

        if (newCompany.id_company !== whatsappCompanyId) {
          await setLocalCompany(storageCompany, companyIds, whatsappCompanyId)
        }
      }

      const parsedWhatsappNumber = atob(whatsappNumber)

      const localUser = getLocalUser()

      let makeLogin = false

      if (!!localUser) {
        sessionStorage.clear()
        removeLocalItem("user")
        setUserSelectedAddress(null)
        setLocalItem("userSelectedAddress", null, true)
        userSelectedAddressRef.current = null
        setUser(null)
        setOrder({ ...order, benefits: [] })

        makeLogin = true
      } else {
        makeLogin = true
      }

      // Salvar cl_token antes do login para não perder o param quando a URL for limpa
      const clToken = searchParams.get("cl")
      if (clToken) sessionStorage.setItem("cl_token", clToken)

      if (makeLogin) {
        setTabSelected("home")

        onSignIn(parsedWhatsappNumber.replace("55", ""), null, true)
          .then(async () => {
            // Disparar visit após login concluído — para todo visitante
            SiteEventController.reportEvent(siteSessionId.current, "visit", null, clToken || null).catch(() => {})

            const trackingId = searchParams.get("r")

            if (trackingId !== null) {
              const orderUid = atob(trackingId)

              const actualOrder = await OrderController.fetchOne(orderUid)

              if (userRef.current) {
                setTabSelected("profile")

                showOrderProgressContextRef.current = { status: true, orderUid: orderUid, content: actualOrder.data }
                setUpdateOrders(!updateOrders)
              }
            }
          })
          .finally(() => {
            setForceLoading(false)

            window.history.pushState({}, document.title, "/" + "")
          })
      }
    }

    // Disparar visit para todo visitante (cl_token opcional se veio de campanha)
    if (!whatsappNumber) {
      const clToken = searchParams.get("cl")
      if (clToken) sessionStorage.setItem("cl_token", clToken)
      SiteEventController.reportEvent(siteSessionId.current, "visit", null, clToken || null).catch(() => {})
    }

    const parsedCompanyId = parseInt(companyIdString)
    await setLocalCompany(storageCompany, companyIds, !!parsedCompanyId && !isNaN(parsedCompanyId) ? parsedCompanyId : null)
  }

  const setEditAddressRef = (editAddress) => {
    editAddressRef.current = editAddress
  }

  const removeMenuOption = (option) => {
    let localMenuOptions = getLocalItem("menuOptions", true)
    if (!!localMenuOptions) {
      if (option !== "products") {
        const removeIndex = localMenuOptions.findIndex(({ id }) => id === option)
        localMenuOptions.splice(removeIndex, 1)
      } else {
        localMenuOptions = localMenuOptions.filter(({ id }) => ["highlights", "combos", "beverages"].includes(id))
      }

      setMenuOptions(localMenuOptions)
      menuOptionsRef.current = localMenuOptions
      setLocalItem("menuOptions", localMenuOptions, true)
    }
  }

  const updateMenuOptions = (newOptions = []) => {
    // Ordenando por posições definidas
    let newMenuOptions = newOptions.sort((a, b) => a.position - b.position) || []

    setMenuOptions(newMenuOptions)
    menuOptionsRef.current = newMenuOptions
    setLocalItem("menuOptions", newMenuOptions, true)
  }

  const addMenuOption = (content) => {
    let newMenuOptions = menuOptions
    // Consultando dados do armazenamento local
    const localMenuOptions = getLocalItem("menuOptions", true)

    if (!!localMenuOptions && localMenuOptions.length > 0) {
      const indexMenu = localMenuOptions.findIndex(({ id }) => id === content.id)

      if (indexMenu !== -1) {
        localMenuOptions.splice(indexMenu, 1)
      }

      newMenuOptions = [...localMenuOptions, content]
    } else {
      const indexMenu = newMenuOptions.findIndex(({ id }) => id === content.id)

      if (indexMenu !== -1) {
        newMenuOptions.splice(indexMenu, 1)
      }

      newMenuOptions.push(content)
    }

    // Ordenando por posições definidas
    newMenuOptions.sort((a, b) => a.position - b.position)

    setMenuOptions(newMenuOptions)
    menuOptionsRef.current = newMenuOptions
    setLocalItem("menuOptions", newMenuOptions, true)
  }

  const verifyAndMoveToTargetElement = (id) => {
    const element = document.getElementById(id)
    const headerOffset = 45
    const elementPosition = element.getBoundingClientRect().top

    const offsetPosition = elementPosition + document.getElementById("section-home").scrollTop - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    })
  }

  const updateMenu = (event, id) => {
    const newMenuOptions = menuOptionsRef.current.map((menu) => {
      return { ...menu, focus: false }
    })

    const indexMenu = newMenuOptions.findIndex((menu) => menu.id === id)

    if (indexMenu !== -1) {
      newMenuOptions[indexMenu] = {
        ...newMenuOptions[indexMenu],
        focus: true,
      }
    }

    menuOptionsRef.current = newMenuOptions

    if (!!document.getElementById(id)) {
      verifyAndMoveToTargetElement(id)

      // document.getElementById(id).scrollIntoView({ top: 20, behavior: "smooth", block: "start", inline: "start" })
    } else {
      window.scrollTo(0, 0)
    }

    setMenuOptions(menuOptionsRef.current)
  }

  //Atualizar dados do endereço do cliente
  const onChangeUserSelectedAddress = async (address = null, finishOrder = false, currentSettings, defaultAddress = false) => {
    let newAddress = { ...address }
    const newSettings = !!currentSettings ? currentSettings : settings
    if (!!newSettings || (Array.isArray(newSettings) && newSettings.length > 0)) {
      try {
        let deliveryFee = 0
        let deliveryTime = 0

        const responseDeliveryCalculate = await DeliveryFeeController.calculate(
          address.customer_address.geo_coordinates[0],
          address.customer_address.geo_coordinates[1],
          !address.neighbourhood.id_neighbourhood
            ? neighbourhoods.find(
                (neighbourhood) => escapeSpecialChart(neighbourhood.nm_neighbourhood) === escapeSpecialChart(address.neighbourhood.nm_neighbourhood)
              )?.id_neighbourhood
            : address.neighbourhood.id_neighbourhood,
          1,
          address?.customer_address?.id_customer_address
        )

        switch (responseDeliveryCalculate.status) {
          case 200:
            deliveryFee = parseFloat(responseDeliveryCalculate.data.val_fee.toFixed(2))
            deliveryTime = responseDeliveryCalculate.data.num_delivery_time

            if (!!order.payments) {
              const { discountType, discount } = getDiscountType(1, false)
              const discountOrder = getDiscountOrder({ ...order, discountType, discount }, order.subTotal, deliveryFee, settings, 1)
              const orderPayment = order.payments.find(({ nm_payment_method }) => nm_payment_method?.toLowerCase() !== "cashback")
              const cashBackPayment = order.payments.find(({ nm_payment_method }) => nm_payment_method?.toLowerCase() === "cashback")

              if (!!orderPayment) {
                if (
                  orderPayment.val_payment !== order.subTotal + deliveryFee - discountOrder - (!!cashBackPayment ? cashBackPayment.val_payment : 0) ||
                  orderPayment.id_payment_method === 1
                ) {
                  if (
                    orderPayment.id_payment_method !== 1 ||
                    (orderPayment.id_payment_method === 1 &&
                      (orderPayment.val_receivable >
                        order.subTotal + deliveryFee - discountOrder - (!!cashBackPayment ? cashBackPayment.val_payment : 0) ||
                        orderPayment.val_receivable === 0))
                  ) {
                    let payments = [
                      {
                        ...orderPayment,
                        val_payment: order.subTotal + deliveryFee - discountOrder - (!!cashBackPayment ? cashBackPayment.val_payment : 0),
                      },
                    ]
                    if (!!cashBackPayment) {
                      payments.push(cashBackPayment)
                    }

                    setOrder({
                      ...order,
                      discountType,
                      discount,
                      payments,
                    })
                  } else {
                    setOrder({ ...order, discountType, discount, payments: [] })
                    setSelectedPaymentMethod({
                      id_payment_method: defaultPaymentMethodIdRef.current.id_payment_method,
                      nm_payment_method: defaultPaymentMethodIdRef.current.nm_payment_method,
                    })
                    setShowPaymentTypeSelector(true)
                    onMessageDialog()
                      .fire({
                        title: "Devido a alteração no valor do pedido, informe o novo valor de troco!",
                        icon: "info",
                        timer: 3000,
                        showConfirmButton: false,
                        showCancelButton: false,
                      })
                      .then()
                  }
                }
              }
            }

            setUserSelectedAddress({ ...address, deliveryTime, deliveryFee })
            setLocalItem("userSelectedAddress", { ...address, deliveryTime, deliveryFee }, true)
            userSelectedAddressRef.current = { ...address, deliveryTime, deliveryFee }
            newAddress = { ...address, deliveryTime, deliveryFee }
            return newAddress
          case 204:
            if (!defaultAddress) {
              onMessageDialog("error")
                .fire({
                  title: `Não atendemos na sua localização. Escolha outro endereço para entrega.`,
                  icon: "warning",
                  timer: 3000,
                  showConfirmButton: false,
                  showCancelButton: false,
                })
                .then()

              if (companies.length > 1 && modalCompanies.status === false) {
                setModalOrderPreview(false)
                setOrder({
                  customer: null,
                  combos: [],
                  products: [],
                  beverages: [],
                  payments: [],
                  benefits: [],
                  total: 0.0,
                  subTotal: 0.0,
                  discount: 0,
                  discountType: "R$",
                  deliveryFee: 0,
                  deliveryComments: "",
                })
                setModalCompanies({ status: true, content: companies })
              }
            }
            setUserSelectedAddress(null)
            setLocalItem("userSelectedAddress", null, true)
            userSelectedAddressRef.current = null

            return false
          default:
            onMessageDialog("error")
              .fire({
                title: "Ocorreu um erro ao consultar a taxa de entrega, entre em contato com o estabelecimento!",
                icon: "warning",
                timer: 3000,
                showConfirmButton: false,
                showCancelButton: false,
              })
              .then()
            return false
        }
        // })
      } catch (error) {
        console.info(error)
        return false
      }
    }
  }

  const getDeliveryFees = (settings, companyUid = null) => {
    deliveryFeesRef.current = []
  }

  const getStartHour = (workingHour) => {
    const startHour = workingHour.start_hour.toString().length === 1 ? "0" + workingHour.start_hour.toString() : workingHour.start_hour.toString()
    const startMinute =
      workingHour.start_minute.toString().length === 1 ? "0" + workingHour.start_minute.toString() : workingHour.start_minute.toString()
    return startHour + ":" + startMinute + ":00"
  }

  const getEndHour = (workingHour) => {
    const endHour = workingHour.end_hour.toString().length === 1 ? "0" + workingHour.end_hour.toString() : workingHour.end_hour.toString()
    const endMinute = workingHour.end_minute.toString().length === 1 ? "0" + workingHour.end_minute.toString() : workingHour.end_minute.toString()
    return endHour + ":" + endMinute + ":00"
  }

  const getSecondsOfHour = (hour) => {
    return hour.split(":").reduce((acc, time) => 60 * acc + +time)
  }

  const getWorkingHour = (date) => {
    try {
      if (!!companyRef.current && !!companyRef.current.company_working_hours) {
        let currentDate = new moment()
        let currentSecondsHour = getSecondsOfHour(getFormatDate(currentDate).getHour("HH:mm:ss"))
        const startHour = getStartHour(companyRef.current.company_working_hours)
        const startHourSeconds = getSecondsOfHour(startHour)
        const endHour = getEndHour(companyRef.current.company_working_hours)
        const endHourSeconds = getSecondsOfHour(endHour)
        let endDate = new moment()
        let startDate = new moment()

        if (!!date) {
          currentDate = new moment(date)
          currentSecondsHour = getSecondsOfHour(getFormatDate(currentDate).getHour("HH:mm:ss"))
        }
        if (startHourSeconds < currentSecondsHour) {
          startDate = moment(currentDate.format("YYYY-MM-DD " + startHour))
          if (endHourSeconds < currentSecondsHour && endHourSeconds < startHourSeconds) {
            endDate = moment(
              moment(currentDate)
                .add(1, "days")
                .format("YYYY-MM-DD " + endHour)
            )
          } else {
            endDate = moment(moment(currentDate).format("YYYY-MM-DD " + endHour))
          }
        } else {
          // if (currentSecondsHour < startHourSeconds && currentSecondsHour > endHourSeconds) {
          //   startDate = moment(currentDate.format("YYYY-MM-DD " + startHour))
          // } else {
          startDate = moment(
            moment(currentDate)
              .subtract(1, "days")
              .format("YYYY-MM-DD " + startHour)
          )
          // }
          if (endHourSeconds > currentSecondsHour && endHourSeconds > startHourSeconds) {
            endDate = moment(
              moment(currentDate)
                .add(1, "days")
                .format("YYYY-MM-DD " + endHour)
            )
          } else {
            endDate = moment(moment(currentDate).format("YYYY-MM-DD " + endHour))
          }
        }

        return { startDate, endDate }
      } else {
        throw new Error("Horários de funcionamento (company_working_hours) não definidos.")
      }
    } catch (error) {
      const currentDate = new moment()
      const startDate = moment(moment(currentDate).format("YYYY-MM-DD " + "00:00:00"))
      const endDate = moment(
        moment(currentDate)
          .add(1, "days")
          .format("YYYY-MM-DD " + "04:00:00")
      )
      return { startDate, endDate }
    }
  }

  const getMercadoPagoPublicKey = async () => {
    await loadMercadoPago()

    setLoading(true)

    const publicKeyRequest = await MercadoPagoController.getPublicKey()

    if (publicKeyRequest.status === 200) {
      const mp = new window.MercadoPago(publicKeyRequest.data)

      setMercadoPagoInstance(mp)
    }

    setLoading(false)
  }

  const showPopupMessage = (popupData, popupStatus) => {
    if (!!popupData) {
      if (!!popupStatus) {
        if (popupStatus[popupData.uuid_popup] === true) {
          setPopup(popupData)
        } else if (popupStatus[popupData.uuid_popup] === false) {
          //
        } else {
          setPopup(popupData)
        }
      } else {
        setPopup(popupData)
      }
    }
  }

  const onSignOut = () => {
    sessionStorage.clear()
    removeLocalItem("user")
    setUserSelectedAddress(null)
    setUser(null)
    setOrder({ ...order, benefits: [] })
    removeCookie("customerToken")
  }

  //Função para logar no sistema
  const onSignIn = async (username, password = null, loginWhatsapp = false) => {
    setLoading(true)

    return CustomerController.signIn(encrypt(username, "base64"), encrypt(password, "base64"), loginWhatsapp)
      .then((response) => {
        if (response.status === 200) {
          setModalSignIn({ status: false })
          const user = response.data

          const userToSave = {
            ...user,
            addresses: [],
            fidelities: [],
          }

          if (userToSave.token) {
            setCookie("customerToken", userToSave.token)

            delete userToSave.token
          }

          // setAddressesLoading(true)
          setUser({ ...userToSave })
          userRef.current = { ...userToSave }
          setLocalItem("user", { ...userToSave, movements_account: [] }, true)

          CustomerController.fetchMe({
            withOrders: true,
            withStreets: true,
            withMovements: true,
            withFidelities: true,
            withContacts: true,
            withLastOrder: true,
          }).then(async (responseCustomer) => {
            if (responseCustomer.status === 200) {
              const addresses = responseCustomer.data?.streets || []
              const fidelities = responseCustomer.data?.customer_fidelities_points || []
              const movements_account = responseCustomer.data?.movements_account || []
              const last_active_order = responseCustomer.data?.last_active_order || null

              const cashbackUsed = getLocalItem("cashbackUsed")

              if (movements_account.length > 0 && cashbackUsed) {
                setLocalItem("movementsAccount", movements_account, true)

                const [dateUsed, val_used] = cashbackUsed.split("__")
                for (const movement of movements_account) {
                  const { type_movement_customer_account, dt_deleted_at } = movement
                  if (!dt_deleted_at && type_movement_customer_account === "output") {
                    const { dt_created_at } = movement
                    const sameDay = moment(dateUsed).isSame(dt_created_at, "D")
                    const sameMonth = moment(dateUsed).isSame(dt_created_at, "M")
                    const sameYear = moment(dateUsed).isSame(dt_created_at, "Y")
                    if (sameDay && sameMonth && sameYear) {
                      const { val_movement } = movement
                      if (parseFloat(Number.parseFloat(val_used).toFixed(2)) === val_movement) {
                        removeLocalItem("cashbackUsed")
                      }
                    }
                  }
                }
              }
              const dt_birth = responseCustomer.data?.dt_birth || ""
              const customer_orders = responseCustomer.data?.customer_orders

              if (addresses.length > 0) {
                const defaultAddress =
                  addresses.find(({ customer_address: { st_primary, dt_deleted_at } }) => st_primary && !dt_deleted_at) || addresses[0]

                if (
                  userSelectedAddress?.nm_street &&
                  modality !== 2 &&
                  modality !== 3 &&
                  !!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)
                ) {
                  const selectedAddress = addresses.find((address) => {
                    return address.customer_address.id_customer_address === userSelectedAddress.customer_address.id_customer_address
                  })

                  if (selectedAddress) {
                    onChangeUserSelectedAddress(selectedAddress).then()
                  }
                }

                if (
                  !userSelectedAddress?.nm_street &&
                  modality !== 2 &&
                  modality !== 3 &&
                  !!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)
                ) {
                  setModality(1)
                  setLocalItem("modality", 1)
                  onChangeUserSelectedAddress(defaultAddress, false, null, true).then()
                }
              }

              setUser({ ...userToSave, customer_orders, addresses, dt_birth, fidelities, movements_account, last_active_order })
              userRef.current = { ...userToSave, customer_orders, addresses, dt_birth, fidelities, movements_account, last_active_order }
              setLocalItem("user", { ...userToSave, customer_orders, addresses, fidelities, movements_account, last_active_order }, true)
            }
          })
        } else if (response.status === 403) {
          onMessageDialog("warning")
            .fire({
              title: `Login não autorizado, entre em contato com a loja!`,
              icon: "error",
              timer: 5000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        } else {
          if (!loginWhatsapp) {
            onMessageDialog("success")
              .fire({
                title: `Usuário ou senha incorretos! 😟`,
                icon: "error",
                timer: 2000,
                showCancelButton: false,
                showConfirmButton: false,
              })
              .then()
          }
        }
      })
      .finally(() => {
        setLoading(false)
        return false
      })
  }

  const onChangeStatusCompany = (st_website) => {
    const storageCompany = getLocalItem("company", true)

    setCompany({ ...storageCompany, st_website })
    companyRef.current = { ...storageCompany, st_website }
    setLocalItem("company", { ...storageCompany, st_website }, true)
  }

  const onChangeOrder = (content) => {
    setSessionItem("order", content, true)
    const [total, subTotal, benefit] = getTotalOrder(content, userSelectedAddressRef.current?.deliveryFee)
    setOrder({
      ...order,
      ...content,
      total,
      subTotal: subTotal + benefit,
    })
  }

  const onChangeFavicon = (link) => {
    let favicon = document.querySelector('link[rel="icon"]')
    if (favicon !== null) {
      favicon.href = link
    } else {
      favicon = document.createElement("link")
      favicon.rel = "icon"
      favicon.href = link
      document.head.appendChild(favicon)
    }
  }

  const onChangeTitlePage = (title) => {
    document.title = title
  }

  const normalizeCoordinates = (coords) => {
    if (!coords) return null

    let lat = null
    let lng = null

    if (coords.latitude !== undefined && coords.latitude !== null) {
      lat = parseFloat(coords.latitude)
    } else if (coords.lat !== undefined && coords.lat !== null) {
      lat = parseFloat(coords.lat)
    }

    if (coords.longitude !== undefined && coords.longitude !== null) {
      lng = parseFloat(coords.longitude)
    } else if (coords.lng !== undefined && coords.lng !== null) {
      lng = parseFloat(coords.lng)
    }

    if (isNaN(lat) || isNaN(lng)) {
      return null
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.error("Coordenadas fora dos limites válidos:", { lat, lng })
      return null
    }

    return {
      latitude: lat,
      longitude: lng,
    }
  }

  const validateAddressFields = (place) => {
    const missingFields = []

    if (!place.street || place.street.trim() === "") {
      missingFields.push("logradouro")
    }
    if (!place.neighbourhood || place.neighbourhood.trim() === "") {
      missingFields.push("bairro")
    }
    if (!place.city || place.city.trim() === "") {
      missingFields.push("cidade")
    }
    if (!place.state || place.state.trim() === "") {
      missingFields.push("estado")
    }

    return missingFields
  }

  const onPlaceSelected = async (place, feature) => {
    setLoading(true)

    if (!place) {
      setLoading(false)
      onMessageDialog()
        .fire({
          title: "Não conseguimos identificar o endereço selecionado, tente novamente!",
          icon: "info",
          timer: 3000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
      return
    }

    let addressInfo = normalizeCoordinates(place.coordinates)

    if (!addressInfo) {
      setLoading(false)
      console.error("Coordenadas inválidas ou ausentes:", place.coordinates)
      onMessageDialog()
        .fire({
          title: "Não conseguimos identificar a localização exata do endereço. Por favor, use o mapa para marcar sua localização.",
          icon: "warning",
          timer: 4000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
      return
    }

    const { latitude, longitude } = addressInfo

    // Criar ou usar feature compatível com MiniMap
    let featureUse = feature

    // Se não existe feature ou não tem a estrutura esperada pelo Mapbox MiniMap
    if (!featureUse || !featureUse.properties?.context) {
      // Criar GeoJSON Feature no formato esperado pelo AddressMinimap
      featureUse = buildSyntheticAddressFeature({
        street: place.street,
        neighbourhood: place.neighbourhood,
        city: place.city,
        state: place.state,
        zipcode: place.zipcode,
        number: place.number,
        coordinates: { latitude, longitude },
        extraProperties: { mapbox_id: `generated_${Date.now()}` }, // ID único para compatibilidade
      })
    }

    // Criar objeto padronizado com todas as informações do endereço
    const standardizedPlace = {
      ...place,
      coordinates: {
        latitude,
        longitude,
        lat: latitude, // Manter compatibilidade
        lng: longitude, // Manter compatibilidade
      },
    }

    if (!standardizedPlace.street || !standardizedPlace.city || !latitude || !longitude) {
      setLoading(false)
      console.error("Validação final falhou - dados incompletos:", standardizedPlace)
      onMessageDialog()
        .fire({
          title: "Erro ao processar endereço. Por favor, tente novamente ou use o mapa para marcar sua localização.",
          icon: "error",
          timer: 4000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
      return
    }

    setModalMinimapAddress({
      status: true,
      content: {
        latitude,
        longitude,
        feature: featureUse,
        place: standardizedPlace,
        withoutNumber: place.withoutNumber,
      },
    })

    setLoading(false)
  }

  const onConfirmLocation = async (addressDetails) => {
    if (!!location) {
      try {
        if (!!addressDetails) {
          await onUpdateContentFullAddress(addressDetails)
        } else {
          setModalClientAddressNotFound({ status: true, address: { latitude, longitude } })
        }
      } catch (error) {
        console.error("Erro ao buscar endereço:", error)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
      onMessageDialog()
        .fire({
          title: "Não conseguimos obter a sua localização, tente novamente!",
          icon: "info",
          timer: 3000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
    }
  }

  const onConfirmNumber = (number, address) => {
    const addressUpdated = {
      ...address,
      customer_address: { ...address.customer_address, num_address: number },
    }

    let editNeighbourhood = false

    if (addressUpdated.neighbourhood.nm_neighbourhood === addressUpdated.neighbourhood.city.nm_city) {
      editNeighbourhood = true

      onMessageDialog()
        .fire({
          title: `Detectamos que o nome do seu bairro é igual ao nome da sua cidade, favor alterar o bairro.`,
          icon: "warning",
          timer: 0,
          showCancelButton: false,
          showConfirmButton: true,
        })
        .then()
    }

    setModalFinalAddress({ status: true, address: addressUpdated, editNeighbourhood })
  }

  const onConfirmAddress = async (addressConfirmed) => {
    const sourceAddress = modalInitialAddress?.content?.source

    switch (sourceAddress) {
      case "mainOptions":
        setModality(1)
        break
      case "orderFinisher":
        setModality(1)
        break
      case "orderPreview":
        setModality(1)
        break
      case "newAddress":
        onUpdateCustomerAddress({ ...addressConfirmed })
        break
      default:
        if (!!editAddressRef.current && editAddressRef.current?.source === "addressesInfoOptions") {
          onUpdateCustomerAddress({ ...addressConfirmed })
          setModality(1)
        }
        break
    }

    setModalInitialAddress({ status: false, content: {} })
    setModalMapAddress({ status: false, location: {} })
    setModalNumberAddress({ status: false, address: {} })
    // setModalFinalAddress({ status: false, address: {}, editNeighbourhood: false })

    await onChangeUserSelectedAddress({ ...addressConfirmed })
  }

  // Função para atualizar o conteúdo de um endereço inteiro
  const onUpdateContentFullAddress = async (response) => {
    // console.info("CHEGOU NA UPDATE CONTENT FULL ADDRESS", response)

    setLoading(true)
    try {
      let { street = null, neighbourhood = null, zipcode, city = null, state = null, country, coordinates, number, address } = response

      if (!street && !neighbourhood && !city && !state && !!address) {
        street = address?.nm_street
        state = address?.neighbourhood?.city?.state?.nm_state
        city = address?.neighbourhood?.city?.nm_city
        neighbourhood = address?.neighbourhood?.nm_neighbourhood
      }

      let states = []

      if (states.length === 0) {
        const resultStates = await getStates()
        if (resultStates) {
          setStates(resultStates)
          setLocalItem("states", resultStates, true)

          states = resultStates
        }
      }

      // Os dois estados são alimentados ao montar o componente.
      if (!!states) {
        const [nm_street, id_street_type, nm_type] = await getStreetName(street)
        const [id_state, uuid_state, nm_state, ds_acronym] = getState(states, state)

        if (!!uuid_state) {
          const [id_city, uuid_city, nm_city] = await getCity(city, uuid_state)

          if (!!uuid_city) {
            const [id_neighbourhood, uuid_neighbourhood, nm_neighbourhood, neighbourhoodList] = await getNeighbourhood(neighbourhood, uuid_city)

            // Verificando se endereço é atendido pelo estabelecimento
            const location = coordinates || {}

            const deliveryFeeType = settings?.display_mode_fee

            const address = {
              zipcode,
              number,
              id_street: null,
              nm_street,
              street_type: { id_street_type, nm_type },
              neighbourhood: {
                id_neighbourhood,
                uuid_neighbourhood,
                nm_neighbourhood,
                city: {
                  id_city,
                  uuid_city,
                  nm_city,
                  state: {
                    id_state,
                    uuid_state,
                    nm_state,
                    ds_acronym,
                  },
                },
              },
              customer_address: {
                geo_coordinates: [location.latitude, location.longitude],
                num_address: editAddressRef.current?.num_address || number || "",
                ds_address_additional_info: editAddressRef.current?.ds_address_additional_info || "",
                ds_observation: editAddressRef.current?.ds_observation || "",
                st_primary: true,
                ds_title: editAddressRef.current?.ds_title || "",
              },
              withoutNumber: response.withoutNumber,
            }

            if (deliveryFeeType === "neighborhood" && nm_neighbourhood === nm_city) {
              delete address.neighbourhood.uuid_neighbourhood
              delete address.neighbourhood.id_neighbourhood
            } else {
              const formatedNeighbourhoodList = neighbourhoodList.map((neighbourhood) => ({
                ...neighbourhood,
                city: { id_city, uuid_city, nm_city, state: { id_state, uuid_state, nm_state, ds_acronym } },
              }))
              setNeighbourhoods(formatedNeighbourhoodList)
            }

            setModalFinalAddress({ status: true, address })
          } else {
            onMessageDialog("info")
              .fire({
                title: "Não conseguimos identificar sua cidade, tente novamente!",
                icon: "info",
                timer: 3000,
                showConfirmButton: false,
                showCancelButton: false,
              })
              .then()
          }
        } else {
          onMessageDialog()
            .fire({
              title: "Não conseguimos identificar seu estado, tente novamente!",
              icon: "info",
              timer: 3000,
              showConfirmButton: false,
              showCancelButton: false,
            })
            .then()
        }
      }
    } catch (error) {
      onMessageDialog("info")
        .fire({
          title: "Não conseguimos identificar seu endereço, tente novamente!",
          icon: "info",
          timer: 3000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
      console.error("error", error)
    } finally {
      setLoading(false)
    }
  }

  // Funcão para verificar a entraga das outras lojas do assinante
  const verifySubscriberDeliveryArea = async (address, location) => {
    //Caso não seja realizada entrega no endereço do cliente, verifica a área de entrega das demais lojas do assinante para redirecionar.
    let otherCompanyDeliveryFees = []
    let subscriberCompanies = []
    for (const otherCompany of companies) {
      if (otherCompany.id_company !== company.id_company) {
        otherCompanyDeliveryFees = await getDeliveryFees(settings, otherCompany.uuid_company)

        let onDeliveryArea = null
        const deliveryFeeType = settings?.display_mode_fee

        if (deliveryFeeType === "neighborhood") {
          onDeliveryArea = !!otherCompanyDeliveryFees
            ? otherCompanyDeliveryFees.find(({ neighbourhood }) => neighbourhood.uuid_neighbourhood === address.neighbourhood.uuid_neighbourhood)
            : null
        } else {
          const companyCoordinates =
            !!company.company_address && company.company_address.length > 0
              ? {
                  latitude: company.company_address[0].geo_coordinates[0],
                  longitude: company.company_address[0].geo_coordinates[1],
                }
              : { latitude: -27.582736076873427, longitude: -48.61364576177011 }

          const customerCoordinates = { latitude: location?.lat, longitude: location?.lng }

          const distance = getDistanceCoordinates(companyCoordinates, customerCoordinates)
          onDeliveryArea = otherCompanyDeliveryFees.find((deliveryFee) => deliveryFee.nro_km_fee >= Math.round(distance))
        }

        if (!!onDeliveryArea || (deliveryFeeType === "neighborhood" && !address.neighbourhood.nm_neighbourhood)) {
          // setModalNumberAddress({ status: true, address })
          subscriberCompanies.push(otherCompany)
        }
      }
    }

    return subscriberCompanies
  }

  const onUpdateCustomerAddress = (address) => {
    setAddressesLoading(true)

    let addresses = [...user.addresses]
    if (!!editAddressRef.current && editAddressRef.current?.source === "addressesInfoOptions") {
      addresses = user.addresses.filter(
        ({ customer_address }) => customer_address?.id_customer_address !== editAddressRef.current?.customer_address?.id_customer_address
      )
      editAddressRef.current = null
    }

    const originalListAddresses = addresses.map((address) => {
      return { ...address, st_primary: false, deleted: true }
    })

    CustomerController.update(user.uuid_customer, {
      addresses: [
        ...originalListAddresses,
        {
          ...address,
          st_primary: true,
          edited: true,
        },
      ],
    })
      .then((responseAddressUpdate) => {
        if (responseAddressUpdate.status === 200) {
          const addresses = responseAddressUpdate.data?.streets || []
          if (addresses.length > 0) {
            const defaultAddress = addresses.find(({ st_primary, dt_deleted_at }) => st_primary && !dt_deleted_at) || addresses[0]
            // if (!userSelectedAddress.nm_street) {

            onChangeUserSelectedAddress(defaultAddress).then()
            // }
          }

          setUser({ ...user, addresses })
          userRef.current = { ...user, addresses }
          setLocalItem("user", { ...user, addresses }, true)

          onMessageDialog("success")
            .fire({
              title: "Endereços atualizados com sucesso!",
              icon: "success",
              timer: 3000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        } else {
          onMessageDialog("danger")
            .fire({
              title: "Não conseguimos atualizar os seus endereços, tente novamente!",
              icon: "error",
              timer: 3000,
              showCancelButton: false,
              showConfirmButton: true,
              confirmButtonText: "Confirmar",
              reverseButtons: true,
            })
            .then()
        }
      })
      .catch(() => {
        onMessageDialog("danger")
          .fire({
            title: "Não conseguimos atualizar os seus endereços, tente novamente!",
            icon: "error",
            timer: 3000,
            showCancelButton: false,
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            reverseButtons: true,
          })
          .then()
      })
      .finally(() => {
        setAddressesLoading(false)
      })
  }

  const onConfirmOrderSchedule = (scheduleInfo) => {
    scheduleInfoRef.current = scheduleInfo
    setModalOrderFinisher(true)
  }

  const clearOrder = () => {
    setOrder({
      customer: null,
      combos: [],
      products: [],
      beverages: [],
      payments: [],
      benefits: [],
      total: 0.0,
      subTotal: 0.0,
      comments: "",
      discount: order?.discount || "0,00",
      discountType: order?.discountType || "R$",
      deliveryFee: 0,
      deliveryComments: "",
    })
    scheduleInfoRef.current = null
    finalizingOrderRef.current = false

    setModalOrderPreview(false)
    setModalOrderSchedule(false)
    setModalOrderFinisher(false)
    setModalPaymentMethods({ status: false, content: null })

    setFinalizingOrder(false)
    removeSessionItem("order")
  }

  const syncCashbackPayment = (orderToUpdate) => syncCashbackPaymentHelper({ order: orderToUpdate, user, settings, modality, userSelectedAddress })

  const preFinishOrder = async (scheduleInfo = null) => {
    if (finalizingOrderRef.current === false) {
      finalizingOrderRef.current = true
      setFinalizingOrder(true)
      setModalOrderProcess({ status: true, resend: false, newRequest: false, scheduleInfo })

      const onlinePaymentMethod = order.payments.find((p) => [37, 38].includes(p.id_payment_method))
      const paymentType = onlinePaymentMethod?.id_payment_method === 38 ? "pix" : onlinePaymentMethod?.id_payment_method === 37 ? "creditcard" : null
      const hasOnlinePayment = !!onlinePaymentMethod

      if (!!scheduleInfo) {
        finalizingOrderRef.current = false
        setFinalizingOrder(false)
        return finishOrder("schedule", scheduleInfo, false, hasOnlinePayment, paymentType)
      }

      let { startDate, endDate } = await getWorkingHour()
      if (!!startDate && !!endDate) {
        // Verificando se o cliente já possui um pedido no dia de corrente
        OrderController.countOrdersByCustomer(user.uuid_customer, endDate.format("YYYY-MM-DD HH:mm:ss"), startDate.format("YYYY-MM-DD HH:mm:ss"))
          .then((response) => {
            if (response.status === 200) {
              if (response.data > 0) {
                finalizingOrderRef.current = false
                setFinalizingOrder(false)
                setModalOrderProcess({
                  status: true,
                  resend: true,
                  newRequest: true,
                  scheduleInfo,
                  content: {
                    onlinePayment: hasOnlinePayment,
                    typeOnlinePayment: paymentType,
                  },
                })
              } else {
                finalizingOrderRef.current = false
                setFinalizingOrder(false)
                finishOrder("direct", null, false, hasOnlinePayment, paymentType)
              }
            } else {
              onMessageDialog("danger")
                .fire({
                  title: response?.data?.message || "Ocorreu um erro ao processar o pedido, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
            }
          })
          .catch((error) => {
            console.error("error", error)
            onMessageDialog("danger")
              .fire({
                title: "Ocorreu uma falha ao processar o pedido, tente novamente!",
                icon: "error",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                reverseButtons: true,
              })
              .then()
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
          })
      } else {
        onMessageDialog("danger")
          .fire({
            title: "Erro ao processar o pedido, por favor, tente novamente!",
            icon: "error",
            timer: 3000,
            showCancelButton: false,
            showConfirmButton: false,
            reverseButtons: true,
          })
          .then()
        finalizingOrderRef.current = false
        setFinalizingOrder(false)
        setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
      }
    }
  }

  //Função para finalizar o pedido
  const finishOrder = async (
    orderType = "direct",
    scheduleInfo = null,
    st_resended = false,
    onlinePayment = false,
    typeOnlinePayment = null,
    newRequest = false
  ) => {
    if (finalizingOrderRef.current === false) {
      finalizingOrderRef.current = true
      setFinalizingOrder(true)

      setModalOrderProcess({ status: true, resend: false, newRequest: false, scheduleInfo })

      if (modality !== 2 && modality !== 3) {
        if (!userSelectedAddressRef.current) {
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: user },
            message: "Pedido não enviado, faltou informar o endereço",
          }).then()
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

          onMessageDialog()
            .fire({
              title: "Estamos quase lá, só faltou informar o seu endereço...",
              icon: "info",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          return false
        }

        //Verificando se entrega na área selecionada.
        let onDeliveryArea = null
        await DeliveryFeeController.calculate(
          userSelectedAddressRef.current.customer_address.geo_coordinates[0],
          userSelectedAddressRef.current.customer_address.geo_coordinates[1],
          userSelectedAddressRef.current.neighbourhood.id_neighbourhood,
          modality,
          userSelectedAddressRef.current.customer_address.id_customer_address
        ).then((response) => {
          switch (response.status) {
            case 200:
              onDeliveryArea = response.data
              break
            case 204:
              onMessageDialog("warning")
                .fire({
                  title: "Infelizmente não entregamos nessa localização!",
                  icon: "warning",
                  timer: 3000,
                  showConfirmButton: false,
                  showCancelButton: false,
                })
                .then()
              break
            default:
              onMessageDialog("error")
                .fire({
                  title: "Ocorreu um erro ao consultar a taxa de entrega, entre em contato com o estabelecimento!",
                  icon: "warning",
                  timer: 3000,
                  showConfirmButton: false,
                  showCancelButton: false,
                })
                .then()
          }
        })
        if (!onDeliveryArea) {
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: user, deliveryFeesRef: deliveryFeesRef.current },
            message: "Pedido não enviado, endereço não atendido",
          }).then()
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

          onMessageDialog()
            .fire({
              title: "Infelizmente não atendemos no seu endereço! 😕",
              icon: "warning",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          return false
        }

        if (
          !!userSelectedAddressRef.current &&
          (!userSelectedAddressRef.current?.customer_address?.geo_coordinates[0] ||
            !userSelectedAddressRef.current?.customer_address?.geo_coordinates[1])
        ) {
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: user, userSelectedAddressRef: userSelectedAddressRef.current },
            message: "Pedido não enviado, endereço não confirmado(sem coordenadas)",
          }).then()

          onMessageDialog("info")
            .fire({
              title: `Para prosseguir é necessário confirmar seu endereço!`,
              icon: "info",
              confirmButtonText: "Confirmar",
              showConfirmButton: true,
              reverseButtons: true,
              showCancelButton: false,
            })
            .then(async ({ isConfirmed }) => {
              if (isConfirmed) {
                setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
                const mapboxSuggestions = await getMapboxSuggestionsByName(
                  getLabelAddress(userSelectedAddressRef.current, { withNumber: true }, company?.company_address[0]?.geo_coordinates)
                )
                if (!!mapboxSuggestions) {
                  const addressCoordinates = await getAddressCoordinates(mapboxSuggestions[0].mapbox_id)
                  // Só aceita se bairro/cidade baterem com o endereço já confirmado.
                  const areaOk = isSameAddressArea(addressCoordinates, {
                    neighbourhood: userSelectedAddressRef.current?.neighbourhood?.nm_neighbourhood,
                    city: userSelectedAddressRef.current?.neighbourhood?.city?.nm_city,
                  })

                  if (!!addressCoordinates && areaOk) {
                    const { latitude, longitude } = addressCoordinates
                    setModalMapAddress({
                      status: true,
                      location: { latitude, longitude },
                    })
                    editAddressRef.current = { ...userSelectedAddressRef.current?.customer_address }
                  } else {
                    setModalInitialAddress({ status: true, content: { source: "orderFinisher" } })
                  }
                } else {
                  setModalInitialAddress({ status: true, content: { source: "orderFinisher" } })
                }
              }
            })
          finalizingOrderRef.current = false
          setFinalizingOrder(false)
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

          return false
        }
      }

      //Verificando se o total do pedido confere com o valor em estado para saber se os itens continuam corretos

      const [total, subTotal, benefitTotal] = getTotalOrder(order, userSelectedAddress?.deliveryFee)

      const deliveryFeeValue = modality !== 2 && modality !== 3 ? userSelectedAddress?.deliveryFee || 0 : 0

      const discount = getDiscountOrder(order, subTotal, deliveryFeeValue, settings, modality)

      const subTotalOrder = total + deliveryFeeValue - benefitTotal - discount

      // Verificando se existe forma de pagamento selecionada e se o total do pagamento é o mesmo do pedido
      let orderInsert = { ...order }

      // ############## VALIDAÇÕES DOS PAGAMENTOS
      // Verificar se existe ao menos 1 pagamento
      if (order.payments && order.payments.length === 0) {
        setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
        SupportController.throwNewLog({
          type: "warning",
          content: { customer: userRef.current },
          message: "Pedido não enviado, Falha na confirmação dos pagamentos",
        }).then()
        onMessageDialog()
          .fire({
            title: "Selecione ao menos uma forma de pagamento e tente reenviar o pedido! 😕",
            icon: "warning",
            timer: 4000,
            showCancelButton: false,
            showConfirmButton: false,
          })
          .then()
        finalizingOrderRef.current = false
        setFinalizingOrder(false)
        setShowPaymentTypeSelector(true)

        return false
      }
      // Verificar se o valor do(s) pagamento(s) é igual ao valor do subtotal do pedido
      let orderPayment = null
      let cashBackPayment = null
      let totalPaymentValidation = 0

      // Separa o pagamento normal do pagamento em cashback e calculando o valor total pago
      for (const payment of order.payments || []) {
        totalPaymentValidation += payment.val_payment

        if (payment.nm_payment_method?.toLowerCase() === "cashback") {
          cashBackPayment = payment
        } else {
          orderPayment = payment
        }
      }

      //Caso o valor total a ser pago esteja diferente do valor total do pedido, é possível ajustar o valor da forma de pagamento em algumas situações
      //Para que o pedido não fique travado.
      if (subTotalOrder !== totalPaymentValidation) {
        let requestPayment = false

        if (!orderPayment && cashBackPayment) {
          // Se só tem cashback e o valor não bate com o total do pedido
          if (cashBackPayment.val_payment < subTotalOrder) {
            // Erro: O saldo de cashback selecionado não cobre o total e não há outro método
            setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
            SupportController.throwNewLog({
              type: "warning",
              content: { customer: userRef.current, orderPayment },
              message: "O saldo de cashback não é suficiente para o total do pedido. Adicione outra forma de pagamento.",
            }).then()
            onMessageDialog()
              .fire({
                title: "O saldo de cashback não é suficiente para o total do pedido. Adicione outra forma de pagamento.",
                icon: "warning",
                timer: 4000,
                showCancelButton: false,
                showConfirmButton: false,
              })
              .then()
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setShowPaymentTypeSelector(true)

            return false
          }
        }

        if (orderPayment) {
          if (
            // É qualquer método de pagamento
            orderPayment?.id_payment_method !== 1 ||
            //Pagamento é em dinheiro E o valor do pagamento é maior que o valor do pedido
            (orderPayment?.id_payment_method === 1 &&
              (orderPayment?.val_receivable > subTotalOrder ||
                // Pagamento não possui valor a receber (Isto ocorre quando é preenchido qualquer forma de pagamento diferente de dinheiro
                // Pois nesse caso nós não informamos que o valor a receber, apenas o valor pago.)
                orderPayment?.val_receivable === 0))
          ) {
            let payments = [
              {
                ...orderPayment,
                val_payment: subTotalOrder - (cashBackPayment?.val_payment || 0),
              },
            ]
            if (!!cashBackPayment) {
              payments.push(cashBackPayment)
            }
            //Alterando o order
            orderInsert = {
              ...order,
              payments,
            }
          } else {
            requestPayment = true
          }
        } else {
          if (cashBackPayment && formatBoolean(settings?.allow_order_paid_only_cashback || false)) {
            const totalDiference = subTotalOrder - totalPaymentValidation
            if (Number.isNaN(totalDiference)) {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              SupportController.throwNewLog({
                type: "warning",
                content: { customer: userRef.current, orderPayment },
                message: "Pedido não enviado, falha na confirmação dos pagamentos",
              }).then()
              onMessageDialog()
                .fire({
                  title: "Selecione novamente sua forma de pagamento e tente reenviar o pedido! 😕",
                  icon: "warning",
                  timer: 4000,
                  showCancelButton: false,
                  showConfirmButton: false,
                })
                .then()
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
              setShowPaymentTypeSelector(true)

              return false
            }
            if (Math.abs(totalDiference) < 0.01) {
              cashBackPayment.val_payment = subTotalOrder
              orderInsert = { ...order, payments: [cashBackPayment] }
            }
            // Se a diferença for maior que um centavo, o saldo de cashback é insuficiente
            else if (totalDiference > 0) {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              onMessageDialog().fire({
                title: "O saldo de cashback não cobre o total do pedido. Por favor, adicione outra forma de pagamento para completar o valor.",
                icon: "warning",
                timer: 5000,
              })

              finalizingOrderRef.current = false
              setFinalizingOrder(false)
              setShowPaymentTypeSelector(true)
              return false
            }
            // Se o cashback for MAIOR que o pedido (totalDiference negativo), ajustamos para baixo (sobra saldo pro cliente)
            else {
              cashBackPayment.val_payment = subTotalOrder
              orderInsert = { ...order, payments: [cashBackPayment] }
            }
          } else {
            requestPayment = true
          }
        }
        if (requestPayment) {
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: userRef.current, orderPayment },
            message: "Pedido não enviado, Falha na confirmação dos pagamentos",
          }).then()
          onMessageDialog()
            .fire({
              title: "Selecione novamente sua forma de pagamento e tente reenviar o pedido! 😕",
              icon: "warning",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          setOrder({ ...order, payments: [] })
          setSelectedPaymentMethod({
            id_payment_method: defaultPaymentMethodIdRef.current.id_payment_method,
            nm_payment_method: defaultPaymentMethodIdRef.current.nm_payment_method,
          })
          setShowPaymentTypeSelector(true)

          return false
        }
      }

      // Verificar se existe apenas pagamentos de cashback e se a configuração de permitir pedidos pagos apenas com cashback está valida (allow_order_paid_only_cashback)
      const cashbackPaymentValue = cashBackPayment ? cashBackPayment?.val_payment || 0 : 0
      const orderTotal = subTotalOrder - cashbackPaymentValue
      orderInsert = {
        ...orderInsert,
        discountType:
          modality === 2 ? settings.pickup_discount_type : modality === 3 ? settings.indoor_discount_type : settings.delivery_discount_type,
        discount:
          modality === 2
            ? parseFloat(settings.pickup_discount_value || 0)
            : modality === 3
            ? parseFloat(settings.indoor_discount_value || 0)
            : parseFloat(settings.delivery_discount_value || 0),
        orderTotal,
      }

      if (![3, 2].includes(modality)) {
        let addresses = [...userRef.current.addresses]

        if (!!userSelectedAddressRef.current.customer_address.id_customer_address) {
          addresses = userRef.current.addresses.filter(
            ({ customer_address }) => customer_address?.id_customer_address !== userSelectedAddressRef.current.customer_address.id_customer_address
          )
        }

        const originalListAddresses = addresses.map((address) => {
          if (!address.customer_address?.st_primary) return address

          return { ...address, st_primary: false, primary_changed: true }
        })

        if (!!userSelectedAddressRef.current.customer_address.dt_deleted_at) {
          userSelectedAddressRef.current = {
            ...userSelectedAddressRef.current,
            customer_address: { ...(userSelectedAddressRef?.current?.customer_address || {}), dt_deleted_at: null, id_customer_address: null },
            st_primary: true,
          }
        }

        const selectedAddressAlreadyPrimary =
          !!userSelectedAddressRef.current.customer_address.id_customer_address && !!userSelectedAddressRef.current.customer_address.st_primary

        CustomerController.update(userRef.current.uuid_customer, {
          addresses: [...originalListAddresses].concat([
            {
              ...userSelectedAddressRef.current,
              st_primary: true,
              primary_changed: !selectedAddressAlreadyPrimary,
            },
          ]),
        })
          .then(async (responseAddressUpdate) => {
            if (responseAddressUpdate.status === 200) {
              const addresses = responseAddressUpdate.data?.streets || []

              const defaultAddress = addresses.find(({ customer_address: { st_primary, dt_deleted_at }, nm_street }) => {
                return st_primary && !dt_deleted_at
              })

              if (!!defaultAddress && defaultAddress.id_street && defaultAddress?.neighbourhood?.id_neighbourhood) {
                let updatedUser = { ...userRef.current, addresses }
                setUser(updatedUser)
                userRef.current = updatedUser
                const addressUpdated = await onChangeUserSelectedAddress(defaultAddress, true)
                if (!addressUpdated) {
                  SupportController.throwNewLog({
                    type: "warning",
                    content: { customer: userRef.current, addressUpdated, defaultAddress },
                    message: "Pedido não enviado, Erro na atualização do cliente",
                  }).then()
                  setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

                  onMessageDialog()
                    .fire({
                      title: "Falha ao verificar o processamento do seu endereço, tente novamente!",
                      icon: "warning",
                      timer: 4000,
                      showCancelButton: false,
                      showConfirmButton: false,
                    })
                    .then()
                  finalizingOrderRef.current = false
                  setFinalizingOrder(false)

                  return false
                }

                setLocalItem("user", updatedUser, true)
                const customer = {
                  defaultAddress,
                  userSelectedAddress: {
                    ...addressUpdated,
                  },
                  uuid_customer: userRef.current.uuid_customer,
                  nm_customer: userRef.current.nm_customer,
                  id_street_customer: defaultAddress.id_street,
                  id_customer_address: defaultAddress?.customer_address?.id_customer_address,
                  id_neighbourhood: defaultAddress?.neighbourhood?.id_neighbourhood,
                }

                orderInsert = {
                  ...orderInsert,
                  id_modality: modality,
                  deliveryFee: !!addressUpdated.deliveryFee ? formatFloat(addressUpdated.deliveryFee) : 0,
                  deliveryTime: !!addressUpdated.deliveryTime ? addressUpdated.deliveryTime : Number(settings?.delivery_time) || 0,
                  st_resended,
                }
                insertOrder(orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest)
              } else {
                SupportController.throwNewLog({
                  type: "warning",
                  content: { customer: userRef.current, defaultAddress },
                  message: "Pedido não enviado, Falha ao identificar o endereço",
                }).then()
                setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

                onMessageDialog()
                  .fire({
                    title: "Não conseguimos identificar o seu endereço, por favor selecione novamente! 😕",
                    icon: "warning",
                    timer: 4000,
                    showCancelButton: false,
                    showConfirmButton: false,
                  })
                  .then()
                finalizingOrderRef.current = false
                setFinalizingOrder(false)
              }
            } else {
              setUser({ ...getLocalItem("user", true) })
              userRef.current = { ...getLocalItem("user", true) }
              SupportController.throwNewLog({
                type: "error",
                content: { customer: userRef.current, responseAddressUpdate },
                message: "Pedido não enviado, falha ao atualizar o endereço",
              }).then()
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

              onMessageDialog("danger")
                .fire({
                  title: "Não conseguimos atualizar o seu endereço, por favor tente novamente! (1)",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: true,
                  confirmButtonText: "Confirmar",
                  reverseButtons: true,
                })
                .then()
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
            }
          })
          .catch((error) => {
            console.error("error", error)

            SupportController.throwNewLog({
              type: "error",
              content: { customer: userRef.current, errorMessage: error.message },
              message: "Pedido não enviado, catch da atualização do cliente",
            }).then()
            setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

            onMessageDialog("danger")
              .fire({
                title: "Não conseguimos atualizar os dados para realizar o pedido, tente novamente!",
                icon: "error",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: false,
              })
              .then()
            finalizingOrderRef.current = false

            setFinalizingOrder(false)
          })
      } else if ([3, 2].includes(modality)) {
        const customer = {
          uuid_customer: userRef.current.uuid_customer,
          nm_customer: userRef.current.nm_customer,
          id_street_customer: null,
          id_customer_address: null,
          id_neighbourhood: null,
        }
        orderInsert = {
          ...orderInsert,
          id_modality: modality,
          deliveryFee: 0,
          deliveryTime: modality === 2 ? Number(settings.pickup_time) : Number(settings.indoor_time),
          st_resended,
        }

        insertOrder(orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest)
      }
    }
  }

  const insertOrder = async (orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest, retry = false) => {
    // Enriquece customer com email/phone para o match de Conversion API server-side.
    customer = { ...customer, ...getCustomerEmailPhone(userRef.current?.contacts || []) }

    const isPixelInitialized = !!activePixelId.pixelId
    const pixelEventId = isPixelInitialized ? generateUuid() : null

    // Browser pixel com eventID compartilhado com Conversion API para dedup.
    // newOrder.val_subtotal só vem para pedidos normais; agendados caem no fallback.
    // isPixelActive() reavaliado na resposta: cobre o pixel que terminou de inicializar
    // durante o request (nesse caso pixelEventId é null e o track sai sem dedup — ok,
    // pois o server-side também não disparou). parseFloat preserva val_subtotal 0 legítimo.
    const trackPurchase = (newOrder) => {
      if (!isPixelActive()) return
      const serverSubtotal = parseFloat(newOrder?.val_subtotal)
      const purchaseValue = Number.isFinite(serverSubtotal) ? serverSubtotal : computeApiValSubtotal(orderInsert) || 0
      trackPixelEvent("Purchase", { value: purchaseValue, currency: "BRL" }, pixelEventId)
    }

    if (onlinePayment) {
      switch (typeOnlinePayment) {
        case "pix":
          console.info("isPixelInitialized", isPixelInitialized)
          const parsedOnlinePaymentOrder = getParamsToCreateOrder(orderInsert, customer, isPixelInitialized, pixelEventId)
          // const onlinePaymentEntry = orderInsert.payments?.find((p) => [37, 38].includes(p.id_payment_method))
          // if (onlinePaymentEntry) {
          //   parsedOnlinePaymentOrder.val_total = onlinePaymentEntry.val_payment
          // }
          const scheduleOrder = orderType === "schedule" && !!scheduleInfo ? scheduleInfo : null
          PaymentsController.createPayment(parsedOnlinePaymentOrder, null, customer, "pix", newRequest, scheduleOrder).then((response) => {
            if (response.status === 422) {
              setModalOrderProcess({ status: true, resend: true, newRequest: true, scheduleInfo, content: { onlinePayment, typeOnlinePayment } })
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
            } else if (response.status === 200) {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              setModalPixPayment({ status: true, content: response.data })
              modalPixPaymentContent.current = response.data
              clearOrder()
            } else {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              finalizingOrderRef.current = false
              setFinalizingOrder(false)

              const isStoreClosed = response.status === 400 && response.data?.message === "Pagamento não pode ser criado pois a loja está fechada!"

              if (isStoreClosed) {
                onChangeStatusCompany(false)
                getSchedules(formatBoolean(settings?.allow_order_schedule_with_closed_company) ? true : false).then(({ schedules }) => {
                  if (!!schedules) setLocalItem("schedules", schedules, true)
                })
              }

              onMessageDialog("danger")
                .fire({
                  title: isStoreClosed
                    ? "A loja está fechada no momento. Tente novamente mais tarde!"
                    : response.status === 400 && !!response.data?.message
                    ? response.data.message
                    : "Falha ao gerar o pagamento online, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
            }
          })
        default:
          break
      }
    } else {
      const content = { orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest }

      OrderController.insert(orderInsert, customer, orderType, scheduleInfo, isPixelInitialized, pixelEventId)
        .then((response) => {
          if (response.status === 201) {
            const newOrder = response.data

            setModalOrderProcess({
              status: false,
              resend: false,
              newRequest: false,
              scheduleInfo: null,
              content: { onlinePayment, typeOnlinePayment },
            })

            if (orderType === "schedule") {
              // Sinalizando o registro de um novo pedido agendado
              console.info("EMITINDO EVENTO [Home] [schedule_order]")
              !!socket &&
                socket.emit("schedule_order", {
                  origin: "site",
                  companyId: company.id_company,
                })

              console.info("EMITINDO EVENTO [Home] [signal_schedule_order]")
              !!socket &&
                socket.emit("signal_schedule_order", {
                  origin: "site",
                  companyId: company.id_company,
                })

              trackPurchase(newOrder)
              const clTokenSched = sessionStorage.getItem("cl_token")
              SiteEventController.reportEvent(
                siteSessionId.current,
                "purchase",
                {
                  uuid_order: newOrder.uuid_schedule_order,
                  val_total: newOrder.val_subtotal,
                },
                clTokenSched || null
              ).catch(() => {})
              if (clTokenSched) sessionStorage.removeItem("cl_token")

              onMessageDialog("sucess")
                .fire({
                  title: "Pedido agendado com sucesso!",
                  icon: "success",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                })
                .then(({ isDismissed }) => {
                  if (isDismissed) {
                    // goToOrderProgressRef.current = { status: false, scheduleOrderUid: newOrder.uuid_schedule_order }
                    showOrderProgressContextRef.current = { status: false, scheduleOrderUid: newOrder.uuid_schedule_order }

                    setTabSelected("profile")
                  }
                })
            } else {
              //Atualizando usuário da sessão setUser(updatedUser)
              const updatedUser = {
                ...userRef.current,
                customer_orders: [
                  {
                    fk_id_customer_address: response?.data?.customer_address_order?.id_customer_address,
                    id_order: response?.data?.id_order,
                    id_street_customer: response?.data?.id_street_customer,
                    st_confirmed: false,
                    dt_deleted_at: null,
                  },
                ],
                last_active_order: {
                  ...newOrder,
                  status_name: newOrder.order_progress[0].ds_progress,
                  status_color: newOrder.order_progress[0].step_color,
                },
              }
              setUser(updatedUser)
              userRef.current = updatedUser
              setLocalItem("user", updatedUser, true)

              trackPurchase(newOrder)
              const clTokenOrder = sessionStorage.getItem("cl_token")
              SiteEventController.reportEvent(
                siteSessionId.current,
                "purchase",
                {
                  uuid_order: newOrder.uuid_order,
                  val_total: newOrder.val_subtotal,
                },
                clTokenOrder || null
              ).catch(() => {})
              if (clTokenOrder) sessionStorage.removeItem("cl_token")

              onMessageDialog("sucess")
                .fire({
                  title: "Pedido realizado com sucesso!",
                  icon: "success",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                })
                .then(({ isDismissed }) => {
                  if (isDismissed) {
                    // goToOrderProgressRef.current = { status: true, orderUid: newOrder.uuid_order, order: newOrder }
                    showOrderProgressContextRef.current = { status: true, orderUid: newOrder.uuid_order, content: newOrder }

                    setTabSelected("profile")
                  }
                })
            }

            // if (order.id_fidelity) {
            //   let fidelityUsageControl = getLocalItem("fidelityUsageControl", true) || {}
            //   console.log("fidelityUsageControl", fidelityUsageControl)
            //   // Obtém os pontos atuais do usuário para esta fidelidade
            //   const userFidelityPoints = user.fidelities.filter((point) => {
            //
            //     point.fk_id_fidelity === order.id_fidelity && !point.st_used
            //   })
            //   console.log("userFidelityPoints", userFidelityPoints)
            //   // Determina quantos pontos são necessários para o resgate
            //   const fidelityInfo = fidelities.find((f) => f.id_fidelity === order.id_fidelity)
            //   console.log("fidelityInfo", fidelityInfo)
            //   const pointsNeeded = fidelityInfo ? fidelityInfo.minimum_value : 0
            //   console.log("pointsNeeded", pointsNeeded)
            //
            //   // Marca os pontos como usados (apenas a quantidade necessária para o resgate)
            //   const pointsToUse = userFidelityPoints.slice(0, pointsNeeded)
            //   console.log("pointsToUse", pointsToUse)
            //   if (!fidelityUsageControl[order.id_fidelity]) {
            //     fidelityUsageControl[order.id_fidelity] = []
            //   }
            //
            //   // Armazena os IDs dos pontos específicos que foram usados
            //   fidelityUsageControl[order.id_fidelity].push({
            //     pointsUsed: pointsToUse.map((point) => point.id_fidelity_customer),
            //     usedAt: Date.now(),
            //   })
            //
            //   setLocalItem("fidelityUsageControl", fidelityUsageControl, true)
            // }

            if (order.id_fidelity) {
              const fidelityUsed = fidelities.find(({ id_fidelity }) => order.id_fidelity === id_fidelity)
              if (fidelityUsed) {
                const pointsNeeded = fidelityUsed.minimum_value || 0
                let fidelityUsageControl = getLocalItem("fidelityUsageControl", true) || {}

                let fidelityGroupItemName = null
                // Verifica se o order.id_fidelity faz parte de um grupo
                for (const [key, value] of Object.entries(groupFidelitiesRef.current)) {
                  if (value.includes(order.id_fidelity)) {
                    fidelityGroupItemName = key
                    break
                  }
                }

                // const fidelityGroupInfo = groupFidelitiesRef.current.find((g) => g.id_fidelity === order.id_fidelity)
                const fidelitiesToDeduct =
                  fidelityGroupItemName !== null
                    ? groupFidelitiesRef.current[fidelityGroupItemName]
                    : // ? groupFidelitiesRef.current.filter((g) => g.id_group_fidelity === fidelityGroupInfo.id_group_fidelity).map((g) => g.id_fidelity)
                      [order.id_fidelity]

                fidelitiesToDeduct.forEach((idFidelity) => {
                  const fidelityInfo = fidelities.find((f) => f.id_fidelity === idFidelity)
                  if (!fidelityInfo) return

                  const userFidelityPoints = user.fidelities.filter((point) => point.fk_id_fidelity === idFidelity && !point.st_used)

                  const pointsToUse = userFidelityPoints.slice(0, pointsNeeded)
                  if (!fidelityUsageControl[idFidelity]) {
                    fidelityUsageControl[idFidelity] = []
                  }

                  fidelityUsageControl[idFidelity].push({
                    pointsUsed: pointsToUse.map((p) => p.id_fidelity_customer),
                    usedAt: Date.now(),
                  })
                })

                setLocalItem("fidelityUsageControl", fidelityUsageControl, true)
              } else {
                SupportController.throwNewLog({
                  type: "error",
                  content: {
                    customer: userRef.current,
                    order,
                    errorMessage: `Foi usada uma fidelidade no site, porém ocorreu um erro na rotina. Fidelidade usada:${order.id_fidelity}`,
                  },
                  message: "Pedido não enviado, catch da inserção do pedido",
                }).then()
              }
            }

            if (order.benefits.length > 0) {
              let localBenefitsUsed = getSessionItem("benefitsUsed", true)

              if (!!localBenefitsUsed) {
                localBenefitsUsed.push(order.id_benefit)
              } else {
                localBenefitsUsed = [order.id_benefit]
              }

              setSessionItem("benefitsUsed", localBenefitsUsed, true)
            }

            clearOrder()
          } else if (response.status === 200) {
            if (!retry) {
              onMessageDialog("danger")
                .fire({
                  title: response?.message || "Falha ao verificar se o pedido foi processado, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
            }
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
          } else if (response.status === 400) {
            const isScheduleFull = orderType === "schedule" && response?.data?.message === "Horário esgotado para esta modalidade."

            if (!retry) {
              onMessageDialog("danger")
                .fire({
                  title: response?.data?.message || "Erro ao processar o pedido, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
            }
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            if (isScheduleFull) {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
            } else {
              setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
            }
          } else {
            // if (!retry) {
            //   onMessageDialog("danger")
            //     .fire({
            //       title: response?.data?.message || response?.message || "Ocorreu um erro ao processar o pedido, tente novamente!",
            //       icon: "error",
            //       timer: 3000,
            //       showCancelButton: false,
            //       showConfirmButton: false,
            //       reverseButtons: true,
            //     })
            //     .then()
            // }
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
          }
        })
        .catch((error) => {
          console.error("error", error)
          SupportController.throwNewLog({
            type: "error",
            content: { customer: userRef.current, order, errorMessage: error.message },
            message: "Pedido não enviado, catch da inserção do pedido",
          }).then()
          if (!retry) {
            onMessageDialog("danger")
              .fire({
                title: "Houve uma falha ao processar o pedido, tente novamente!",
                icon: "error",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                reverseButtons: true,
              })
              .then()
          }
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
        })
    }
  }

  const handleOutsideCoverageArea = (value) => {
    setIsOutsideCoverageArea(value)
  }

  const onAddBeverages = (beverages, selectedItem = null) => {
    const colors = [
      { id: 1, used: false, color: "#F2D7D5" },
      { id: 2, used: false, color: "#D4E6F1" },
      { id: 3, used: false, color: "#D4EFDF" },
      { id: 4, used: false, color: "#FDEBD0" },
      { id: 5, used: false, color: "#FADBD8" },
      { id: 6, used: false, color: "#EBDEF0" },
      { id: 7, used: false, color: "#D6EAF8" },
      { id: 8, used: false, color: "#D1F2EB" },
      { id: 9, used: false, color: "#D6DBDF" },
      { id: 10, used: false, color: "#F4ECF7" },
      { id: 11, used: false, color: "#FAE5D3" },
    ]

    if (!!selectedItem) {
      //verificando quantidade de produto com mesmo id
      const amountProduct =
        !!order.products &&
        order.products.reduce((acumulator, product) => {
          return product.uuid_product === selectedItem.products.uuid_product ? acumulator + 1 : acumulator
        }, 0)

      order.products.push({
        ...selectedItem.products,
        nro_product: amountProduct + 1,
        backgroundColor: colors[amountProduct].color,
      })

      for (const beverage of selectedItem.beverages) {
        order.beverages.push({
          ...beverage,
          amount: beverage.amount || 1,
          nro_product: amountProduct + 1,
          backgroundColor: colors[amountProduct].color,
        })
      }
    } else {
      for (const beverage of beverages) {
        const indexBeverage = order.beverages.findIndex(
          ({ id_beverage, id_product, nro_item }) => !nro_item && !id_product && id_beverage === beverage.id_beverage
        )

        if (indexBeverage === -1) {
          order.beverages.push({ ...beverage })
        }
      }
    }

    trackPixelEvent("AddToCart", {})
    const clTokenAddBev = sessionStorage.getItem("cl_token")
    SiteEventController.reportEvent(
      siteSessionId.current,
      "add_to_cart",
      {
        nm_product: selectedItem?.products?.nm_product || null,
        qty: 1,
      },
      clTokenAddBev || null
    ).catch(() => {})
    onChangeOrder({ ...order })
    setModalBuilder({ ...modalBuilder, status: false, content: null, size: null })
    setModalBeverages({ status: false, content: null })
  }

  //CONTENT FUNCTIONS
  const onClickItem = (type, content) => {
    switch (type) {
      case "category":
        setModalBeverages({ status: true, category: content })
        break
      case "beverage":
        // Verificando se já existe a bebida na lista de pedido
        if (
          !order.beverages.find(
            ({ id_beverage, id_product, nro_product = null, nro_combo = null, nm_beverage }) =>
              !id_product && id_beverage === content.id_beverage && nro_product === null && nro_combo === null
          )
        ) {
          const newOrder = { ...order, beverages: [...order.beverages, { ...content, amount: 1 }] }

          const [total] = getTotalOrder(newOrder)
          setOrder(syncCashbackPayment({ ...newOrder, total, subTotal: total }))

          trackPixelEvent("AddToCart", { nm_item: content?.nm_beverage })
          const clTokenBev = sessionStorage.getItem("cl_token")
          SiteEventController.reportEvent(
            siteSessionId.current,
            "add_to_cart",
            {
              nm_product: content?.nm_beverage || null,
              qty: 1,
            },
            clTokenBev || null
          ).catch(() => {})

          GeneralTools.swalMessage("success")
            .fire({
              title: `${getBeverageName(content)} inserida a sua sacola!`,
              icon: "success",
              timer: 2000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        } else {
          GeneralTools.swalMessage("success")
            .fire({
              title: `${getBeverageName(content)} já está em sua sacola!`,
              icon: "info",
              timer: 2000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        }
        break
      case "flavour":
        // const { uuid_product, flavour, productIndex } = content

        //Filtrando os tamanhos que não estão habilitados para o site
        const flavourSizes = content.flavour.sizes.filter(({ st_website }) => st_website).sort((a, b) => a.nro_priority - b.nro_priority)
        setModalBuilder({
          ...modalBuilder,
          type,
          status: true,
          content: {
            ...content,
            size: flavourSizes[0],
          },
        })

        break
      case "highlight":
        const highlightBeverage = content.highlight_beverages.length > 0
        const highlightCombo = content.highlight_combos.length > 0
        const highlightProduct = content.highlight_products.length > 0

        if (highlightBeverage) {
          const beverageSelected = beverages.find(({ id_beverage }) => id_beverage === content.highlight_beverages[0].id_beverage)
          onClickItem("beverage", beverageSelected)
        } else if (highlightCombo) {
          const comboSelected = combos.find(({ id_combo }) => id_combo === content.highlight_combos[0].id_combo)
          setModalBuilder({ status: true, type: "combo", content: { combo: comboSelected } })
        } else if (highlightProduct) {
          const product = content.highlight_products[0].highlights_products
          let productSelected = products.find(({ id_product }) => id_product === product.fk_id_product)

          if (productSelected.st_display_mode === "size") {
            productSelected = {
              ...productSelected,
              size: productSelected.sizes.find(({ id_size }) => id_size === product.fk_id_size),
            }
            delete productSelected.sizes
            setModalBuilder({ status: true, type: "size", content: productSelected })
          } else {
            const highlightFlavour = productSelected.flavours.find(({ id_flavour }) => id_flavour === product.fk_id_flavour)

            setModalBuilder({
              type: "flavour",
              status: true,
              content: {
                size: productSelected.sizes[0],
                flavour: highlightFlavour,
                uuid_product: productSelected.uuid_product,
              },
            })
          }
        }

        break
      default:
        setModalBuilder({ status: true, type, content })
        break
    }
  }

  const onShowModalSchedule = () => {
    if ([3, 2].includes(modality) || !!userSelectedAddress) {
      setModalOrderSchedule(true)
    } else {
      onMessageDialog("info")
        .fire({
          title: "Para agendar um pedido é preciso escolher um endereço",
          icon: "info",
          timer: 3000,
          showCancelButton: false,
          showConfirmButton: false,
        })
        .then()
    }
  }

  const onChangeAmountProduct = (amount, indexProduct) => {
    let products = order.products
    if (amount > 0) {
      let newProducts = products.map((product, index) => {
        if (index === indexProduct) {
          product.amount = amount
          return product
        }
        return product
      })

      let newBeverages = order.beverages.map((beverage, index) => {
        if (index === indexProduct) {
          beverage.amount = amount
          return beverage
        }
        return beverage
      })

      const newOrder = {
        ...order,
        products: newProducts,
        beverage: newBeverages,
      }

      const [total] = getTotalOrder(newOrder)
      setOrder(syncCashbackPayment({ ...newOrder, total, subTotal: total }))
    } else if (amount === 0) {
      GeneralTools.swalMessage("delete")
        .fire({
          title: "Deseja remover o item do carrinho?",
          icon: "question",
          cancelButtonText: "Cancelar",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          showConfirmButton: true,
          reverseButtons: true,
        })
        .then((response) => {
          if (response.isConfirmed) {
            let newProducts = products
              .filter((product, index) => {
                if (index === indexProduct) {
                  return null
                }
                return product
              })
              .filter((eachElement) => Object.keys(eachElement).length > 0)

            const removedOrder = { ...order, products: newProducts }
            setOrder(syncCashbackPayment(removedOrder))
          }
        })
    }
  }

  const findPromotionProduct = (newProduct) => {
    if (newProduct?.uuid_product === "c4beff5d-c3df-481d-88e5-ae2a90c853a0" && newProduct?.id_product === 7) {
      const orderToVerify = { ...order }

      for (const product of orderToVerify.products) {
        if (product.uuid_product === "c4beff5d-c3df-481d-88e5-ae2a90c853a0" && product.id_product === 7) {
          return true
        }
      }
    }

    return false
  } // TEMPORARIO

  const onChangeOrderBuilder = (item, selectedItem, comboRemoved = null) => {
    if (!!item) {
      if ("id_combo" in item) {
        order.combos.push({ ...selectedItem, backgroundColor: getBackgroundColor(currentOrderCombo) })

        if (!!selectedItem.products) {
          for (const product of selectedItem.products) {
            order.products.push({
              ...product,
              amount: 1,
              id_combo: product.id_combo,
              nro_item: product.nro_item,
              nro_combo: product.nro_combo,
            })

            for (const beverage of product.beverages) {
              order.beverages.push({
                ...beverage,
                id_combo: item.id_combo,
                nro_combo: item.nro_combo,
                nro_item: product.nro_item,
              })
            }
          }
        }

        if (!!selectedItem.beverages) {
          for (const beverage of selectedItem.beverages) {
            order.beverages.push({
              ...beverage,
              id_combo: item.id_combo,
              nro_combo: item.nro_combo,
              nro_item: beverage.nro_item,
            })
          }
        }

        // Removendo os dados do combo antigo
        if (!!comboRemoved) {
          const comboIndex = order.combos.findIndex(
            ({ nro_combo, id_combo }) => nro_combo === comboRemoved?.comboNro && id_combo === comboRemoved?.comboId
          )
          if (comboIndex !== -1) {
            order.combos.splice(comboIndex, 1)
            order.products = order.products.filter(
              ({ nro_combo, id_combo }) => nro_combo !== comboRemoved?.comboNro || id_combo !== comboRemoved?.comboId
            )
            order.beverages = order.beverages.filter(
              ({ nro_combo, id_combo }) => nro_combo !== comboRemoved?.comboNro || id_combo !== comboRemoved?.comboId
            )
          }
        }
      } else {
        if (selectedItem.size.beverages.length > 0) {
          setModalBeverages({
            status: true,
            content: null,
            data: {
              isMandatory: selectedItem.size.st_required_beverage,
              maxBeverages: selectedItem.size.qty_beverages,
              selectedItem,
            },
          })
        } else if (selectedItem.size.st_required_beverage) {
          const rawCount = selectedItem.size.qty_beverages_raw ?? 0
          const message = !selectedItem.size.st_beverages_resolved
            ? "Aguarde, ainda estamos carregando as bebidas deste produto."
            : rawCount > 0
            ? "Este produto exige bebida, mas nenhuma está disponível no site. Entre em contato com a loja."
            : "Este produto exige bebida, mas nenhuma foi cadastrada. Entre em contato com a loja."

          GeneralTools.swalMessage("warning")
            .fire({
              title: message,
              icon: "warning",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          return
        } else {
          if (findPromotionProduct(item)) {
            // TEMPORARIO
            GeneralTools.swalMessage("warning")
              .fire({
                title: "Atenção, este produto só pode ser adicionado 1 vez na sacola!",
                icon: "warning",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: false,
              })
              .then()
          } else {
            const amountProduct =
              !!order.products &&
              order.products.reduce((acumulator, product) => {
                return product.fk_id_product === selectedItem.fk_id_product ? acumulator + 1 : acumulator
              }, 0)

            order.products.push({ ...selectedItem, nro_product: amountProduct + 1 })
          }
        }
      }

      const [total, subTotal] = getTotalOrder({ ...order })
      const updatedOrder = syncCashbackPayment({ ...order, total, subTotal })
      setSessionItem("order", updatedOrder, true)
      setOrder(updatedOrder)

      const clTokenBuilder = sessionStorage.getItem("cl_token")
      SiteEventController.reportEvent(
        siteSessionId.current,
        "add_to_cart",
        {
          nm_product: selectedItem?.nm_product || selectedItem?.products?.nm_product || null,
          qty: 1,
        },
        clTokenBuilder || null
      ).catch(() => {})
    } else {
      Message.info("Erro ao identificar o item escolhido")
    }
  }

  // Função para obter a cor de fundo de cada item do pedido
  const getBackgroundColor = (id = null) => {
    // Verificando se uma cor possui um identificador igual
    const indexID = colors.findIndex((item) => item.used === true && item.id === id)
    if (indexID !== -1) {
      return colors[indexID].color
    }

    // Buscando uma cor não utilizada
    const indexColor = colors.findIndex((item) => item.used === false && !!id)
    if (indexColor !== -1) {
      // Atualizando a lista de cores
      colors[indexColor] = { ...colors[indexColor], used: true }
      setColors([...colors])
      return colors[indexColor].color
    }

    // Atualizando a lista de cores
    colors[colors.length - 1] = { ...colors[colors.length - 1], used: true }
    return colors[colors.length - 1].color
  }

  const onUpdate = (company) => {
    setUserSelectedAddress(null)
    setLocalItem("userSelectedAddress", null, true)
    userSelectedAddressRef.current = null
    setModalCompanies({ status: false, content: null })
    fetchInitialData(company, false, true, [], {
      onChangeFavicon,
      onChangeTitlePage,
      addMenuOption,
      updateMenuOptions,
      removeMenuOption,
      onChangeUserSelectedAddress,
    }).then()
  }

  const onSelectedCompany = async (company, showCancelButton = true, text = `A loja está fechada! Deseja prosseguir?`) => {
    if (!company.st_website) {
      let fowardToClosedCompany = false
      await GeneralTools.swalMessage("delete")
        .fire({
          title: text,
          icon: "info",
          showCancelButton: showCancelButton,
          cancelButtonText: "Voltar",
          showConfirmButton: true,
          confirmButtonText: "Prosseguir",
          reverseButtons: true,
        })
        .then(({ isConfirmed }) => {
          fowardToClosedCompany = isConfirmed
        })
      if (!fowardToClosedCompany) return null
    }
    if (!company.url_redirect) {
      // Vai definir que o pedido está fora da área de entrega
      // handleOutsideCoverageArea(isOutsideCoverageArea)
      setLocalItem("company", company)

      // Limpando todos os dados salvos no Storage
      removeLocalItem("menuOptions")
      removeLocalItem("settings")

      removeLocalItem("highlights")
      removeLocalItem("combos")

      removeLocalItem("products")
      removeLocalItem("productSizes")
      removeLocalItem("productFlavours")

      removeLocalItem("beverages")
      removeLocalItem("schedules")

      removeLocalItem("deliveryFee")
      removeLocalItem("deliveryTime")

      clearOrder()

      onUpdate(company)
    } else {
      GeneralTools.swalMessage("delete")
        .fire({
          title: `Você será direcionado ao endereço desta loja, deseja prosseguir?`,
          icon: "info",
          showCancelButton: true,
          cancelButtonText: "Voltar",
          showConfirmButton: true,
          confirmButtonText: "Prosseguir",
          reverseButtons: true,
        })
        .then(({ isConfirmed }) => {
          if (isConfirmed) {
            setLoading(true)
            window.open(company.url_redirect, "_self")
          }
        })
    }
  }

  const setLocalCompany = async (storageCompany, companyIds, whatsappCompanyId = null) => {
    let newCompany = storageCompany
    let setCompanyLocal = false
    let startLoad = false
    let refreshOrder = false
    let companies = getLocalItem("companies", true) || []

    if (companies.length > 0 && !!newCompany && !whatsappCompanyId) {
      setCompanyLocal = true
      refreshOrder = false
      startLoad = true
    } else if (companies.length > 0 && !!newCompany && !!whatsappCompanyId && newCompany.id_company === whatsappCompanyId) {
      setCompanyLocal = true
      refreshOrder = true
      startLoad = false
    } else {
      const responseCompanies = await CompanyController.fetchAll(true, companyIds)

      if (responseCompanies.status === 200) {
        companies = responseCompanies?.data || []
        setLocalItem("companies", companies, true)

        if (!!whatsappCompanyId) {
          newCompany = companies.find((company) => company.id_company === whatsappCompanyId)
        }

        if (!newCompany) {
          newCompany = companies[0]
        }

        if (!storageCompany && !whatsappCompanyId && companies.length > 1) {
          setModalCompanies({ status: true, content: companies })
        } else if (companies.length === 1) {
          onSelectedCompany(companies[0], false, "A Loja está fechada neste momento!")
        }

        setCompanyLocal = true
        startLoad = true
      }
    }

    if (setCompanyLocal) {
      setCompany(newCompany)
      companyRef.current = newCompany
      setLocalItem("company", newCompany, true)
      fetchInitialData(newCompany, startLoad, refreshOrder, companies, {
        onChangeFavicon,
        onChangeTitlePage,
        addMenuOption,
        updateMenuOptions,
        removeMenuOption,
        onChangeUserSelectedAddress,
      }).then() // Obtendo conteúdo do site
    }
  }

  const clearOrderProgressContext = () => {
    showOrderProgressContextRef.current = { status: false, orderUid: null, scheduleOrderUid: null }
  }

  let orderItems = []
  if (!!settings.site_show_order) {
    orderItems = JSON.parse(settings.site_show_order)
  }

  let siteContent = []
  if (orderItems.length > 0) {
    orderItems.forEach((eachItem) => {
      switch (eachItem.name) {
        case "destaques":
          siteContent.push(<Highlights onClickHighlight={onClickItem} key={eachItem.id} />)
          break
        case "combos":
          siteContent.push(<Combos onClickCombo={onClickItem} key={eachItem.id} />)
          break
        case "produtos":
          siteContent.push(<Products onClickProduct={onClickItem} key={eachItem.id} />)
          break
        case "bebidas":
          siteContent.push(<Beverages onClickBeverage={onClickItem} onClickCategory={onClickItem} key={eachItem.id} />)
          break
      }
    })
  } else {
    siteContent = [
      <Highlights onClickHighlight={onClickItem} key={"_highlight"} />,
      <Combos onClickCombo={onClickItem} key={"_combos"} />,
      <Products onClickProduct={onClickItem} key={"_products"} />,
      <Beverages onClickBeverage={onClickItem} onClickCategory={onClickItem} key={"_beverages"} />,
    ]
  }

  const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
      <div className="error-boundary">
        <div className="error-boundary--body">
          <img alt="" src={logoImage} />
          <h6>Erro ao carregar os dados do cardápio virtual.</h6>
          <h6>Favor entrar em contato com o estabelecimento urgente!</h6>
          <div>
            <Label>Descrição do erro</Label>
            <pre>{error.message}</pre>
            <Button onClick={resetErrorBoundary}>Recarregar</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error) => {
        console.error("ERRO AO CARREGAR O SITE => ", error)
      }}
    >
      <React.Suspense
        fallback={
          <div style={{ width: "100%" }} className="app--loading">
            <HomeSkeleton />
          </div>
        }
      >
        <SocketContext.Provider value={{ socket }}>
          <UserContext.Provider value={{ user, setUser, userSelectedAddress, setUserSelectedAddress, setEditAddressRef }}>
            <MenuContext.Provider
              value={{
                menuFixed,
                addMenuOption,
                updateMenuOptions,
                updateMenu,
                setModalSignIn,
                setModalCompanies,
                modalPaymentMethods,
                modalBeverages,
                setModalBeverages,
                modalBuilder,
                setModalBuilder,
                modalInitialAddress,
                setModalInitialAddress,
                modalOrderPreview,
                setModalOrderPreview,
                setModalMapAddress,
                setModalPaymentMethods,
                setModalCompanyContacts,
                showPaymentTypeSelector,
                setShowPaymentTypeSelector,

                menuOptions: menuOptionsRef.current,
              }}
            >
              <CompanyContext.Provider value={{ company, companies, setCompany }}>
                <SettingsContext.Provider
                  value={{
                    version,
                    settings,
                    combos,
                    products,
                    highlights,
                    popup,
                    beverages,
                    schedules,
                    fidelities,
                    setFidelities,
                    neighbourhoods,
                    isOutsideCoverageArea,
                    setIsOutsideCoverageArea,
                    currentStep,
                    setCurrentStep,
                    modality,
                    setModality,
                    onChangeUserSelectedAddress,
                  }}
                >
                  <OrderContext.Provider
                    value={{
                      order,
                      currentOrderCombo,
                      deliveryType,
                      finalizingOrder,
                      setDeliveryType,
                      setCurrentOrderCombo,
                      selectedPaymentMethod,
                      setSelectedPaymentMethod,
                      finishOrder,
                      preFinishOrder,
                      clearOrder,
                      setOrder: onChangeOrder,
                      scheduleInfo: scheduleInfoRef.current,
                    }}
                  >
                    <TabContext.Provider
                      value={{
                        tabSelected,
                        setTabSelected: (tab) => {
                          setSessionItem("order", order, true)
                          setTabSelected(tab)
                        },
                        hideTabs,
                        setHideTabs,
                        goToOrders: goToOrdersRef.current,
                        moveToOrders: (status) => {
                          goToOrdersRef.current = status
                        },
                        goToOrderProgress: goToOrderProgressRef.current,
                        setGoToOrderProgress: (value) => {
                          goToOrderProgressRef.current = value
                        },
                        backToOrderProgress: () => {
                          goToOrderProgressRef.current = { status: false, orderUid: null, scheduleOrderUid: null }
                        },
                        showOrderProgressContext: showOrderProgressContextRef.current,
                        goToFidelity: goToFidelityRef.current,
                        moveToFidelity: () => {
                          goToFidelityRef.current = true
                        },
                        goToCashback: goToCashbackRef.current,
                        moveToCashback: (status) => {
                          goToCashbackRef.current = status
                        },
                        setModalPixPayment: (value) => {
                          setModalPixPayment(value)
                          modalPixPaymentContent.current = value.content
                        },
                        goToOrderProggressAfterOnlinePayment: goToOrderProggressAfterOnlinePayment,
                        goToScheduleAfterOnlinePayment: goToScheduleAfterOnlinePayment,
                        updatePendingOrdersRef: updateOrders,
                        clearOrderProgressContext,
                      }}
                    >
                      <LoadingContext.Provider value={{ loading, setLoading, addressesLoading, setAddressesLoading }}>
                        <MercadoPagoContext.Provider value={{ MercadoPagoInstance }}>
                          <section
                            className={
                              "section-home " +
                              (tabSelected === "home" ? "selected-home " : "") +
                              (!!order && (order.products?.length > 0 || order.beverages?.length > 0) && tabSelected === "home"
                                ? "selected-home-resume-bar "
                                : "")
                            }
                            id={"section-home"}
                          >
                            {errorMessage.length > 0 ? (
                              <div
                                style={{
                                  padding: 20,
                                  marginTop: 40,
                                  color: "#FF0000",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {errorMessage.map((message, key) => (
                                  <h4 key={key}>{message}</h4>
                                ))}
                              </div>
                            ) : (
                              <>
                                {(() => {
                                  switch (tabSelected) {
                                    case "home": {
                                      return (
                                        <>
                                          <Header
                                            setModalCompanies={() =>
                                              setModalCompanies({
                                                status: true,
                                                content: companies,
                                              })
                                            }
                                          />
                                          <Main setModalCompanies={setModalCompanies} />
                                          {isContentLoading ? (
                                            <HomeSkeleton />
                                          ) : (
                                            <React.Suspense fallback={<HomeSkeleton />}>
                                              {siteContent.map((eachItem) => {
                                                return eachItem
                                              })}
                                            </React.Suspense>
                                          )}

                                          <React.Suspense fallback={<h1>Carregando modal</h1>}>
                                            <ModalBuilder
                                              title="Faça suas escolhas"
                                              type={modalBuilder.type}
                                              status={modalBuilder.status}
                                              content={modalBuilder.content}
                                              onLoad={modalBuilder.onLoad}
                                              onChangeOrder={onChangeOrderBuilder}
                                              onChangeAmountProduct={onChangeAmountProduct}
                                              onClose={() => setModalBuilder({ ...modalBuilder, status: false, content: null, size: null })}
                                            />
                                          </React.Suspense>
                                        </>
                                      )
                                    }
                                    case "favorites": {
                                      return null
                                    }
                                    case "cart": {
                                      console.info("cart]")
                                      break
                                    }
                                    case "profile": {
                                      return (
                                        <Profile
                                          orderId={orderSuccessId.current}
                                          loading={loading}
                                          setLoading={setLoading}
                                          userConnected={userIsConnected()}
                                        />
                                      )
                                    }
                                    default: {
                                      return <p>Carregando site</p>
                                    }
                                  }
                                })()}
                              </>
                            )}

                            {!!order && (order.products?.length > 0 || order.beverages?.length > 0) && tabSelected === "home" && (
                              <OrderResumeBar
                                order={order}
                                deliveryFee={!!userSelectedAddress && ![3, 2].includes(modality) ? userSelectedAddress.deliveryFee || 0 : 0}
                                setTab={() => setModalOrderPreview(true)}
                              />
                            )}

                            {/*Modal de lojas*/}
                            <ModalCompanies
                              title="Onde você está?"
                              content={modalCompanies.content}
                              status={modalCompanies.status}
                              earlyAddress={modalCompanies.earlyAddress}
                              handleOutsideCoverageArea={handleOutsideCoverageArea}
                              onSelectedCompany={onSelectedCompany}
                              onRequestAddress={() => setModalInitialAddress({ status: true, content: { source: "mainOptions" } })}
                            />
                            {/*Modal de Login*/}
                            <ModalSignIn
                              status={modalSignIn.status}
                              fullLogin={modalSignIn.fullLogin}
                              origin={modalSignIn.origin}
                              onSignIn={onSignIn}
                              recoverySettings={recoverySettings}
                              onClose={() => {
                                setModalSignIn({ status: false })
                              }}
                            />
                            {/*Modal de Bebidas*/}
                            <ModalBeverages
                              title="Bebidas"
                              content={modalBeverages.content}
                              status={modalBeverages.status}
                              data={modalBeverages.data}
                              category={modalBeverages.category}
                              onClick={onAddBeverages}
                              onClose={() => setModalBeverages({ status: false, content: null })}
                            />
                            {/*Modal para vizualização do pedido*/}
                            <OrderPreview
                              status={modalOrderPreview}
                              openOrderSchedule={onShowModalSchedule}
                              openOrderFinisher={() => {
                                scheduleInfoRef.current = null
                                setModalOrderFinisher(true)
                              }}
                              onClose={() => setModalOrderPreview(false)}
                            />
                            {/*Modal para finalizar o pedido*/}
                            <ModalOrderFinisher
                              status={modalOrderFinisher}
                              finishOrder={finishOrder}
                              onClose={() => setModalOrderFinisher(false)}
                              scheduleInfo={scheduleInfoRef.current}
                            />
                            {/*Modal de processamento do pedido*/}
                            <ModalOrderProcess
                              status={modalOrderProcess.status}
                              resend={modalOrderProcess.resend}
                              content={modalOrderProcess.content}
                              newRequest={modalOrderProcess.newRequest}
                              scheduleInfo={modalOrderProcess.scheduleInfo}
                              setTabSelected={setTabSelected}
                              finishOrder={finishOrder}
                              insertOrder={insertOrder}
                              onClose={() => {
                                setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
                              }}
                            />

                            {/*Modal para finalizar o pedido*/}
                            <ModalOrderSchedule
                              status={modalOrderSchedule}
                              onConfirmOrder={onConfirmOrderSchedule}
                              onClose={() => setModalOrderSchedule(false)}
                              onGetSchedules={getSchedules}
                            />

                            <ModalSuccessOrder
                              content={modalSuccessOrder.content}
                              status={modalSuccessOrder.status}
                              onClose={(orderId) => {
                                orderSuccessId.current = orderId
                                setModalOrderFinisher(false)
                                setModalOrderSchedule(false)
                                setTabSelected("profile")
                                setModalSuccessOrder({ status: false, content: null })
                              }}
                              onBack={() => setModalSuccessOrder({ status: false, content: null })}
                            />

                            {/* Modal para buscar o endereço do cliente ao acessar a página */}
                            <ModalInitialAddress
                              status={modalInitialAddress.status}
                              content={modalInitialAddress.content}
                              onPlaceSelected={onPlaceSelected}
                              getStates={getStates}
                              getCities={getCitiesByState}
                              onShowMap={() => setModalMapAddress({ status: true, location: {} })}
                              onClose={() => setModalInitialAddress({ status: false, title: "", content: null })}
                              setModalMinimapAddress={setModalMinimapAddress}
                            />
                            <ModalMapAddress
                              status={modalMapAddress.status}
                              location={modalMapAddress.location}
                              address={modalMapAddress?.address}
                              onConfirmLocation={onConfirmLocation}
                              onClose={() => setModalMapAddress({ status: false, location: {} })}
                            />
                            <ModalNumberAddress
                              status={modalNumberAddress.status}
                              address={modalNumberAddress.address}
                              onConfirmNumber={onConfirmNumber}
                              onClose={() => setModalNumberAddress({ status: false, address: {} })}
                            />
                            <ModalFinalAddress
                              status={modalFinalAddress.status}
                              address={modalFinalAddress.address}
                              editNeighbourhood={modalFinalAddress.editNeighbourhood}
                              onConfirmAddress={onConfirmAddress}
                              origin={modalFinalAddress.origin}
                              onClose={() => setModalFinalAddress({ status: false, address: {} })}
                            />

                            {/* Modal para os contatos do estabelecimento.*/}
                            {modalCompanyContacts ? <ModalCompanyContacts onClose={() => setModalCompanyContacts(false)} /> : ""}

                            {/* Modal do popup de mensagem */}
                            <ModalPopup status={modalPopup.status} content={popup} onClose={() => setModalPopup({ status: false })} />

                            {/* Modal de bloqueio devido a versão incorreta*/}
                            <ModalBlocked status={modalBlockByOutdatedVersion.status} content={modalBlockByOutdatedVersion.content} />

                            {(loading || addressesLoading || forceLoading) && (
                              <FullContentLoading loading={true} type="fade" title="Aguarde, estamos carregando as informações..." />
                            )}

                            <ModalAddressNotFound
                              status={modalAddressNotFound.status}
                              title={modalAddressNotFound.title}
                              companies={modalAddressNotFound.companies}
                              onUpdate={(company) => {
                                setUserSelectedAddress(null)
                                userSelectedAddressRef.current = null
                                removeLocalItem("userSelectedAddress")
                                setModalInitialAddress({ status: false, title: "", content: null })
                                setModalMapAddress({ status: false, location: {} })
                                setModalAddressNotFound({ status: false, title: "", companies: [] })
                                fetchInitialData(company, false, true, [], {
                                  onChangeFavicon,
                                  onChangeTitlePage,
                                  addMenuOption,
                                  updateMenuOptions,
                                  removeMenuOption,
                                  onChangeUserSelectedAddress,
                                }).then()
                              }}
                              // onConfirmAddress={onConfirmAddress}
                              onClose={() => setModalAddressNotFound({ status: false, title: "", companies: [] })}
                            />

                            <ModalClientAddressNotFound
                              status={modalClientAddressNotFound.status}
                              address={modalClientAddressNotFound.address}
                              onClose={() => {
                                setModalClientAddressNotFound({ status: false, address: null })
                              }}
                              openAddressModal={(address) => {
                                setModalClientAddressNotFound({ status: false, address: null })

                                setModalFinalAddress({ status: true, address, editNeighbourhood: false, origin: "fromMap" })
                              }}
                            />

                            <ModalPixPayment
                              status={modalPixPayment.status}
                              content={modalPixPayment.content}
                              orderProgressRef={goToOrderProgressRef.current}
                              onClose={() => {
                                clearOrder()
                                setModalPixPayment({ status: false, content: null })
                                setTabSelected("profile")
                                goToOrdersRef.current = true
                              }}
                            />

                            {/* Modal para o componente do minimap do mapbox */}
                            <ModalMinimapAddress
                              status={modalMinimapAddress.status}
                              content={modalMinimapAddress.content}
                              onClose={() => {
                                setModalMinimapAddress({ status: false, content: null })
                              }}
                              onConfirmLocation={onConfirmLocation}
                              setModalMinimapAddress={setModalMinimapAddress}
                            />
                            {!!company.id_company && (
                              <FacebookPixelManager
                                key={company.id_company}
                                companyId={company.id_company}
                                activePixelId={activePixelId}
                                setActivePixelId={setActivePixelId}
                              />
                            )}

                            {/*/!*Componente de mensagem de alerta*!/*/}
                            {/*<Alert offset={0} stack={{ limit: 3, spacing: 10 }} contentTemplate={AlertTemplate} />*/}
                          </section>
                          <Footer setModalContentOrder={setModalContentOrder} />

                          <Toaster containerStyle={{ zIndex: 9999 }} />
                        </MercadoPagoContext.Provider>
                      </LoadingContext.Provider>
                    </TabContext.Provider>
                  </OrderContext.Provider>
                </SettingsContext.Provider>
              </CompanyContext.Provider>
            </MenuContext.Provider>
          </UserContext.Provider>
        </SocketContext.Provider>
      </React.Suspense>
    </ErrorBoundary>
  )
}

export default App
